import React, { useState, useEffect, useMemo, useCallback, useRef, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { currentLocale } from '../i18n';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { cleanName } from '../utils/names';
import { useAuth } from '../contexts/AuthContext';
import { useAdvertiser } from '../contexts/AdvertiserContext';
import { formatCurrency } from '../utils/currency';
import CurrencyValue from './CurrencyValue';
import NewCampaignButton from './NewCampaignButton';
import SenseMaxButton from './SenseMaxButton';
import { getBranding } from '../config/branding';

// ─── Sense Max platform logos ────────────────────────────────────────────────
// Real brand marks shown on Sense Max cards ("running on"). Platform names may
// arrive as channel keys (google_ads, meta…) or labels (Google, Meta…) — normalise.
export const PLATFORM_LABEL: Record<string, string> = {
  sense: 'Sense', google: 'Google', meta: 'Meta',
  tiktok: 'TikTok', yandex: 'Yandex', linkedin: 'LinkedIn',
};
export const platformKey = (raw: string): string => {
  const s = (raw || '').toLowerCase().replace(/[^a-z]/g, '');
  if (s.includes('google')) return 'google';
  if (s.includes('meta') || s.includes('facebook') || s.includes('instagram')) return 'meta';
  if (s.includes('tiktok')) return 'tiktok';
  if (s.includes('yandex')) return 'yandex';
  if (s.includes('linkedin')) return 'linkedin';
  return 'sense';
};
// Official-ish brand SVGs (Google 4-colour; Meta/TikTok/LinkedIn from Simple Icons).
const TIKTOK_D = 'M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z';
const PLATFORM_SVG: Record<string, React.ReactElement> = {
  google: (
    <svg viewBox="0 0 48 48" className="plat-svg" aria-hidden="true">
      <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
      <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
      <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
      <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
    </svg>
  ),
  meta: (
    <svg viewBox="0 0 24 24" className="plat-svg" aria-hidden="true">
      <path fill="#0081FB" d="M6.915 4.03c-1.968 0-3.683 1.28-4.871 3.113C.704 9.208 0 11.883 0 14.449c0 .706.07 1.369.21 1.973a6.624 6.624 0 0 0 .265.86 5.297 5.297 0 0 0 .371.761c.696 1.159 1.818 1.927 3.593 1.927 1.497 0 2.633-.671 3.965-2.444.76-1.012 1.144-1.626 2.663-4.32l.756-1.339.186-.325c.061.1.121.196.183.3l2.152 3.595c.724 1.21 1.665 2.556 2.47 3.314 1.046.987 1.992 1.22 3.06 1.22 1.075 0 1.876-.355 2.455-.843a3.743 3.743 0 0 0 .81-.973c.542-.939.861-2.127.861-3.745 0-2.72-.681-5.357-2.084-7.45-1.282-1.912-2.957-2.93-4.716-2.93-1.047 0-2.088.467-3.053 1.308-.652.57-1.257 1.29-1.82 2.05-.69-.875-1.335-1.547-1.958-2.056-1.182-.966-2.315-1.303-3.454-1.303zm10.16 2.053c1.147 0 2.188.758 2.992 1.999 1.132 1.748 1.647 4.195 1.647 6.4 0 1.548-.368 2.9-1.839 2.9-.58 0-1.027-.23-1.664-1.004-.496-.601-1.343-1.878-2.832-4.358l-.617-1.028a44.908 44.908 0 0 0-1.255-1.98c.07-.109.141-.224.211-.327 1.12-1.667 2.118-2.602 3.358-2.602zm-10.201.553c1.265 0 2.058.791 2.675 1.446.307.327.737.871 1.234 1.579l-1.02 1.566c-.757 1.163-1.882 3.017-2.837 4.338-1.191 1.649-1.81 1.817-2.486 1.817-.524 0-1.038-.237-1.383-.794-.263-.426-.464-1.13-.464-2.046 0-2.221.63-4.535 1.66-6.088.454-.687.964-1.226 1.533-1.533a2.264 2.264 0 0 1 1.088-.285z"/>
    </svg>
  ),
  tiktok: (
    <svg viewBox="0 0 24 24" className="plat-svg" aria-hidden="true">
      <path fill="#25F4EE" transform="translate(-0.9,0.5)" d={TIKTOK_D}/>
      <path fill="#FE2C55" transform="translate(0.9,-0.5)" d={TIKTOK_D}/>
      <path fill="#010101" d={TIKTOK_D}/>
    </svg>
  ),
  linkedin: (
    <svg viewBox="0 0 24 24" className="plat-svg" aria-hidden="true">
      <path fill="#0A66C2" d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z"/>
    </svg>
  ),
};
export const PlatformLogo: React.FC<{ name: string }> = ({ name }) => {
  const k = platformKey(name);
  const label = PLATFORM_LABEL[k] || 'Sense';
  let inner: React.ReactNode = PLATFORM_SVG[k];
  let tileCls = 'plat-tile-white';
  if (k === 'yandex') { inner = <span className="plat-letter">Я</span>; tileCls = 'plat-tile-yandex'; }
  else if (k === 'sense') { inner = <span className="plat-letter">S</span>; tileCls = 'plat-tile-sense'; }
  return (
    <span className={`plat-tile ${tileCls}`} title={label} aria-label={label}>
      {inner}
    </span>
  );
};

// Max card budget row. Shows FULL amounts, but switches all four to the compact form
// (229.5k) only when the card is too narrow to fit them — measured via ResizeObserver.
const SmBudgetStrip: React.FC<{ sense: number; ak: number; ext: number; chargedTotal: number; own: boolean; ccy: string }> =
  ({ sense, ak, ext, chargedTotal, own, ccy }) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [compact, setCompact] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setCompact(el.clientWidth < 520);   // full amounts need ~520px across 4 cols
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const V: React.FC<{ v: number; muted?: boolean }> = ({ v, muted }) => (
    <div className={`sm-bs-val ${muted ? 'muted' : ''}`}><CurrencyValue value={v} currency={ccy} short={compact} /></div>
  );
  return (
    <div className="sm-budget-strip" ref={ref}>
      <div className="sm-bs-item"><div className="sm-bs-label">{t('campaigns.platformSpend')}</div><V v={sense} /></div>
      <div className="sm-bs-item"><div className="sm-bs-label">{own ? t('campaigns.external') : t('campaigns.externalStats')}</div><V v={ext} muted={!own} /></div>
      <div className="sm-bs-item"><div className="sm-bs-label">{t('campaigns.commission')}</div><V v={ak} /></div>
      <div className="sm-bs-item total"><div className="sm-bs-label">{t('campaigns.fromBalance')}</div><V v={chargedTotal} /></div>
    </div>
  );
};

interface TrendPoint {
  date: string;
  impressions: number;
  clicks: number;
  spend: number;
  ctr: number;
  conversions: number;
}

// Sum two daily trend series (e.g. Sense leg + external platforms) by date into one series.
const mergeTrendByDate = (a?: TrendPoint[], b?: TrendPoint[]): TrendPoint[] => {
  const m: Record<string, TrendPoint> = {};
  const add = (pts?: TrendPoint[]) => (pts || []).forEach(p => {
    const e = m[p.date] || { date: p.date, impressions: 0, clicks: 0, spend: 0, ctr: 0, conversions: 0 };
    e.impressions += p.impressions || 0;
    e.clicks += p.clicks || 0;
    e.spend += p.spend || 0;
    e.conversions += p.conversions || 0;
    m[p.date] = e;
  });
  add(a); add(b);
  return Object.values(m)
    .map(e => ({ ...e, ctr: e.impressions > 0 ? (e.clicks / e.impressions) * 100 : 0 }))
    .sort((x, y) => (x.date < y.date ? -1 : 1));
};

// cpc/cpm/cpa are derived per day from the trend point (spend/clicks, spend/impressions,
// spend/conversions), not stored on TrendPoint — see getValue in CardSparkline.
type MetricType = 'impressions' | 'clicks' | 'ctr' | 'spend' | 'conversions' | 'cpc' | 'cpm' | 'cpa';

interface DemographicsData {
  age: { range: string; impressions: number; clicks: number; percentage: number }[];
  gender: { gender: string; impressions: number; clicks: number; percentage: number }[];
  total_impressions: number;
  total_reach: number;
  targeting?: {
    age_ranges: string[] | null;
    genders: string[] | null;
  };
  has_data?: boolean;
  message?: string;
}

interface Campaign {
  id: string;
  name: string;
  advertiser: string;
  first_date: string | null;
  last_date: string | null;
  impressions: number;
  clicks: number;
  ctr: number;
  spend: number;
  io_count: number;
  status: 'active' | 'paused' | 'ended' | 'draft';
  start_date: string | null;
  end_date: string | null;
  types: ('display' | 'video' | 'audio')[];
  trend: TrendPoint[];
  impressions_change?: number | null;
  clicks_change?: number | null;
  spend_change?: number | null;
  cpm_change?: number | null;
  cpc_change?: number | null;
  ctr_change?: number | null;
  budget?: number | null;
  has_draft_groups?: boolean;
  conversions: number;
  top_conversions?: { name: string; count: number }[];
  pacing_alert_count?: number;
  budget_needed_count?: number;
  settings_issue_count?: number;
  daily_paused_count?: number;
  media_plan_groups_count?: number;
  pacing_alerts?: Array<{metric: string; expected: number; delivered: number; behind_pct: number; date: string}> | null;
  budget_currency?: string | null;
  is_locked?: boolean;
  bg_pacing_state?: 'FREE' | 'THROTTLE' | 'PAUSED' | null;
  bg_pacing_recalc_at?: string | null;
  // True if this campaign has at least one Google/Meta virtual Ad Group.
  // When true, planned budget is hidden across the campaign — show spend only.
  has_external_data?: boolean;
  // Sense Max draft (lives in sense_max_drafts, opened in the Sense Max constructor).
  sense_max_draft?: boolean;
  sense_max_id?: number;
  // Launched Sense Max campaign: the platforms it was launched to (from the registry).
  sense_max?: boolean;
  sense_max_platforms?: string[];
  // Launched platforms whose cabinet is now disconnected (shown greyed, not hidden).
  disconnected_platforms?: string[];
  // Platforms whose tool FAILED to launch (partial launch) — shown with a red "!" marker.
  failed_platforms?: string[];
  sense_max_draft_id?: number;
}

interface CampaignsResponse {
  campaigns: Campaign[];
  currency?: string;
  counts: {
    all: number;
    active: number;
    paused: number;
    ended: number;
    draft?: number;
  };
}

interface AdGroupTrendPoint {
  date: string;
  impressions: number;
  clicks: number;
  spend: number;
}

interface AdGroup {
  id: string;
  ads_io_id: string | null;  // null for draft groups
  name: string;
  status: 'active' | 'paused' | 'ended' | 'scheduled';
  start_date: string | null;
  end_date: string | null;
  impressions: number;
  clicks: number;
  ctr: number;
  spend: number;
  budget: number | null;
  kpi_amount: number | null;
  first_date: string | null;
  last_date: string | null;
  type: string;
  creative_count: number;
  trend: AdGroupTrendPoint[];
  conversions: number;
  pacing_status?: 'normal' | 'ahead' | 'behind';
  recommended_budget?: number | null;
  pacing_alert?: {metric: string; expected: number; delivered: number; behind_pct: number; date: string} | null;
  budget_currency?: string;
  source?: 'dv360' | 'google' | 'meta';
}

// Check if a draft group can be published (has all required fields)
const canPublishGroup = (group: AdGroup): boolean => {
  if (!group.name?.trim()) return false;
  if (!group.start_date) return false;
  if (!group.end_date) return false;
  if (!group.budget || group.budget === 0) return false;
  if (!group.kpi_amount || group.kpi_amount === 0) return false;
  return true;
};

// 239.1K in English, 239,1 тис. in Ukrainian — the suffix and the decimal mark
// both belong to the language, so Intl does the compacting rather than a hand-rolled /1000.
const formatNumber = (num: number): string => {
  if (num >= 1000) {
    return new Intl.NumberFormat(currentLocale(), { notation: 'compact', maximumFractionDigits: 1 }).format(num);
  }
  return num.toLocaleString(currentLocale());
};

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric', year: 'numeric' });
};

// formatCurrency imported from utils/currency

interface PieSegment {
  range: string;
  percentage: number;
  reach: number;
  path: string;
  color: string;
}

interface PieSegmentWithAngles extends PieSegment {
  startAngle: number;
  endAngle: number;
}

const PieChartWithTooltip: React.FC<{ segments: PieSegment[], size: number }> = ({ segments, size }) => {
  const [hoveredSegment, setHoveredSegment] = useState<PieSegment | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const center = size / 2;
  const outerRadius = 120;
  const innerRadius = 60;
  const labelRadius = (outerRadius + innerRadius) / 2; // Labels in the middle of the donut ring

  // Calculate angles for each segment
  let currentAngle = -90;
  const segmentsWithAngles: PieSegmentWithAngles[] = segments.map(seg => {
    const angle = (seg.percentage / 100) * 360;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    currentAngle = endAngle;
    return { ...seg, startAngle, endAngle };
  });

  const handleMouseMove = (e: React.MouseEvent, seg: PieSegment) => {
    const rect = e.currentTarget.closest('.age-pie-container')?.getBoundingClientRect();
    if (rect) {
      setTooltipPos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top - 10
      });
    }
    setHoveredSegment(seg);
  };

  return (
    <div className="age-pie-container">
      <svg width={size} height={size} className="age-pie-chart">
        {segments.map((seg) => (
          <path
            key={seg.range}
            d={seg.path}
            fill={seg.color}
            className="age-pie-segment"
            onMouseMove={(e) => handleMouseMove(e, seg)}
            onMouseLeave={() => setHoveredSegment(null)}
          />
        ))}
        {/* Labels inside segments */}
        {segmentsWithAngles.map((seg) => {
          if (seg.percentage < 5) return null; // Skip label for very small segments
          const midAngle = (seg.startAngle + seg.endAngle) / 2;
          const midRad = (midAngle * Math.PI) / 180;
          const x = center + labelRadius * Math.cos(midRad);
          const y = center + labelRadius * Math.sin(midRad);
          return (
            <text
              key={`label-${seg.range}`}
              x={x}
              y={y}
              textAnchor="middle"
              dominantBaseline="middle"
              className="age-pie-label"
            >
              {seg.percentage.toFixed(0)}%
            </text>
          );
        })}
      </svg>
      {hoveredSegment && (
        <div
          className="age-pie-tooltip"
          style={{ left: tooltipPos.x, top: tooltipPos.y }}
        >
          {hoveredSegment.range}: {formatNumber(hoveredSegment.reach)}
        </div>
      )}
      <div className="age-pie-legend">
        {segments.map((seg) => (
          <div key={seg.range} className="age-legend-item">
            <span className="age-legend-color" style={{ background: seg.color }}></span>
            <span className="age-legend-label">{seg.range}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const CampaignsPage: React.FC = () => {
  const { t } = useTranslation();
  // Placement type arrives as data ('display' / 'Display'); only its label is translated.
  const adGroupTypeLabel = (type: string): string => {
    const key = (type || '').toLowerCase();
    return ['display', 'video', 'audio'].includes(key) ? t(`campaigns.${key}`) : type;
  };
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isAnalyst = user?.role === 'analyst' || user?.role === 'agency_analyst';
  const { selectedAdvertiserId, selectedAdvertiser, isAllClients, isLoading: advertiserCtxLoading } = useAdvertiser();
  const [currency, setCurrency] = useState(user?.currency || 'EUR');
  const initialFilter = searchParams.get('filter') || 'active';
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [smBudgetMap, setSmBudgetMap] = useState<Record<string, { currency: string; total_budget?: number | null; external_spend: number; external_impressions: number; external_clicks: number; commission: number; commission_percent: number; model_type: string; external_charged: number; trend?: TrendPoint[] }>>({});
  const [counts, setCounts] = useState({ all: 0, active: 0, paused: 0, ended: 0, draft: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>(initialFilter);
  const [searchQuery, setSearchQuery] = useState('');
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [selectedMetrics, setSelectedMetrics] = useState<Record<string, MetricType>>({});
  const [demographicsView, setDemographicsView] = useState<Record<string, boolean>>({});
  const [demographicsData, setDemographicsData] = useState<Record<string, DemographicsData | null>>({});
  const [demographicsLoading, setDemographicsLoading] = useState<Record<string, boolean>>({});
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});

  // Delete campaign state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingCampaign, setDeletingCampaign] = useState<Campaign | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Delete group modal state
  const [showDeleteGroupModal, setShowDeleteGroupModal] = useState(false);
  const [deletingGroup, setDeletingGroup] = useState<AdGroup | null>(null);
  const [isDeletingGroup, setIsDeletingGroup] = useState(false);

  // Duplicate group state
  const [isDuplicatingGroup, setIsDuplicatingGroup] = useState(false);

  // Duplicate campaign state
  const [isDuplicatingCampaign, setIsDuplicatingCampaign] = useState(false);

  // Apply budget recommendation state
  const [applyingBudgetForGroup, setApplyingBudgetForGroup] = useState<string | null>(null);

  // Slide panel state
  const [slidePanel, setSlidePanel] = useState<{ open: boolean; campaign: Campaign | null }>({ open: false, campaign: null });
  const [adGroups, setAdGroups] = useState<AdGroup[]>([]);
  const [adGroupsLoading, setAdGroupsLoading] = useState(false);

  const getSelectedMetric = (campaignId: string): MetricType => selectedMetrics[campaignId] || 'impressions';
  const setSelectedMetric = (campaignId: string, metric: MetricType) => {
    setSelectedMetrics(prev => ({ ...prev, [campaignId]: metric }));
  };

  useEffect(() => {
    // Don't load until the advertiser context has restored its saved
    // selection from localStorage — otherwise the initial '__all__'
    // default fires a fetch that races (and often wins) the filtered
    // fetch that runs once the saved id is applied.
    if (advertiserCtxLoading) return;
    loadCampaigns();
  }, [selectedAdvertiserId, advertiserCtxLoading]);

  // Sense Max cards: fetch the balance breakdown (external spend + commission + model).
  // While it loads, cards must NOT flash the Sense-only numbers then overwrite with the summed
  // ones — smBudgetLoading gates the metric display until the breakdown is in.
  const [smBudgetLoading, setSmBudgetLoading] = useState(false);
  useEffect(() => {
    const maxCamps = campaigns.filter(c => isSenseMax(c) && !c.sense_max_draft && c.status !== 'draft');
    if (!maxCamps.length) { setSmBudgetMap({}); setSmBudgetLoading(false); return; }
    setSmBudgetLoading(true);
    Promise.all(maxCamps.map(c =>
      api.get(`/api/sense-max/campaign/${c.id}/budget`).then(r => [c.id, r.data] as const).catch(() => null)
    )).then(pairs => {
      const m: Record<string, any> = {};
      pairs.forEach(p => { if (p) m[p[0]] = p[1]; });
      setSmBudgetMap(m);
    }).finally(() => setSmBudgetLoading(false));
  }, [campaigns]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdown(null);
    if (openDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdown]);

  // Close slide panel and modals on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showDeleteGroupModal && !isDeletingGroup) {
          setShowDeleteGroupModal(false);
          setDeletingGroup(null);
        } else if (showDeleteModal && !isDeleting) {
          setShowDeleteModal(false);
          setDeletingCampaign(null);
        } else if (slidePanel.open) {
          closeAdGroupsPanel();
        }
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [slidePanel.open, showDeleteModal, isDeleting, showDeleteGroupModal, isDeletingGroup]);

  const loadCampaigns = async () => {
    setLoading(true);
    setError('');
    try {
      // Fetch published campaigns
      const advParam = !isAllClients ? `?advertiser_id=${selectedAdvertiserId}` : '';
      const response = await api.get<CampaignsResponse>(
        `/api/reports/campaigns/list${advParam}`
      );

      // Deduplicate by campaign ID using Map
      const campaignMap = new Map<string, Campaign>();
      response.data.campaigns.forEach(campaign => {
        campaignMap.set(campaign.id, campaign);
      });

      // Fetch draft campaigns from sense_campaigns (not yet published)
      let draftCampaigns: Campaign[] = [];
      try {
        const draftResponse = await api.get(`/api/sense/campaigns${!isAllClients ? `?advertiser_id=${selectedAdvertiserId}` : ''}`);
        const senseCampaigns = draftResponse.data?.campaigns || [];

        // Filter only drafts (no ads_campaign_id = not published). Sense Max Sense-legs
        // (SM_-named) never appear as standalone cards — they belong to their Max campaign.
        draftCampaigns = senseCampaigns
          .filter((c: any) => !c.ads_campaign_id && !(c.display_name || '').startsWith('SM_'))
          .map((c: any) => ({
            id: String(c.id),
            name: c.display_name,
            advertiser: c.advertiser_name || '',
            status: 'draft' as const,
            start_date: c.start_date,
            end_date: c.end_date,
            impressions: 0,
            clicks: 0,
            spend: 0,
            budget: 0,
            ctr: 0,
            cpm: 0,
            cpc: 0,
            trend_data: [],
            types: [t('campaigns.display')],
            io_count: c.io_count || 0
          }));
      } catch (draftErr) {
        console.log('No draft campaigns found');
      }

      // Fetch Sense Max drafts (sense_max_drafts). A NOT-yet-launched draft shows as a
      // draft card. A LAUNCHED draft becomes the single unified campaign card: it takes
      // the real metrics/status from its Sense leg (which we then hide, so the Sense
      // sub-campaign never appears on its own) and keeps the draft's name/platforms.
      let senseMaxDrafts: Campaign[] = [];
      // All clients → ask for every client's drafts, otherwise a Max campaign was
      // simply absent from that list while its Sense leg stayed hidden.
      if (isAllClients || selectedAdvertiserId) {
        try {
          const smAdv = isAllClients ? '__all__' : selectedAdvertiserId;
          const smResp = await api.get(`/api/sense-max/drafts?advertiser_id=${smAdv}`);
          senseMaxDrafts = (smResp.data?.drafts || []).map((d: any) => {
            if (d.launched && d.launched_campaign_id) {
              const legId = String(d.launched_campaign_id);
              const leg = campaignMap.get(legId);
              if (leg) campaignMap.delete(legId);   // hide the raw Sense sub-campaign
              // Spread the leg (real metrics/trend/budget), then override identity + status
              // so the launched draft renders as one unified live campaign card.
              return {
                ...(leg || { id: legId, name: d.name, advertiser: '', status: 'paused',
                             impressions: 0, clicks: 0, ctr: 0, spend: 0, io_count: 0,
                             first_date: null, last_date: null, trend: [], conversions: 0,
                             types: [], start_date: null, end_date: null }),
                id: legId,
                name: d.name || t('campaigns.untitled'),
                status: (d.status || 'paused'),
                start_date: d.start_date || null,
                end_date: d.end_date || null,
                sense_max: true,
                sense_max_draft: false,
                sense_max_draft_id: d.id,
                sense_max_platforms: (d.platforms && d.platforms.length ? d.platforms : ['sense']),
                disconnected_platforms: d.disconnected_platforms || [],
                failed_platforms: d.failed_platforms || [],
              } as Campaign;
            }
            return {
              id: `smdraft-${d.id}`,
              name: d.name || t('campaigns.untitled'),
              advertiser: '',
              status: 'draft' as const,
              start_date: d.start_date || null,
              end_date: d.end_date || null,
              first_date: null,
              last_date: null,
              impressions: 0,
              clicks: 0,
              spend: 0,
              budget: 0,
              ctr: 0,
              trend: [],
              conversions: 0,
              types: [],
              io_count: 0,
              sense_max_draft: true,
              sense_max_id: d.id,
              // Platforms with ≥1 launch-ready format (from the drafts endpoint) → "Running on".
              sense_max_platforms: (d.platforms && d.platforms.length ? d.platforms : ['sense']),
            } as Campaign;
          });
        } catch (smErr) {
          console.log('No Sense Max drafts');
        }
      }

      // Merge campaigns. Any leftover raw Sense Max Sense-leg (SM_-named sense_campaigns
      // not merged into its launched-draft card above) must never surface on its own.
      const uniqueCampaigns = Array.from(campaignMap.values())
        .filter(c => !(c.name || '').startsWith('SM_'));
      const allCampaigns = [...uniqueCampaigns, ...draftCampaigns, ...senseMaxDrafts];

      setCampaigns(allCampaigns);
      if (response.data.currency) setCurrency(response.data.currency);

      // Recompute counts from the FINAL list: a launched Sense Max draft is now a live
      // campaign card (not a draft) and its raw Sense leg was removed, so the backend
      // counts no longer line up — derive them from what we actually render.
      const newCounts = {
        all: allCampaigns.length,
        active: allCampaigns.filter(c => c.status === 'active').length,
        paused: allCampaigns.filter(c => c.status === 'paused').length,
        draft: allCampaigns.filter(c => c.status === 'draft').length,
        ended: allCampaigns.filter(c => c.status === 'ended').length,
      };
      setCounts(newCounts);

      // Auto-switch tab based on available campaigns
      if (newCounts.active > 0) {
        setStatusFilter('active');
      } else if (newCounts.all > 0) {
        setStatusFilter('all');
      }
    } catch (err: any) {
      setError(t('campaigns.loadFailed') + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadDemographics = async (campaignId: string) => {
    if (demographicsData[campaignId]) return; // Already loaded

    setDemographicsLoading(prev => ({ ...prev, [campaignId]: true }));
    try {
      const response = await api.get<DemographicsData>(
        `/api/reports/demographics/campaign/${campaignId}`
      );
      setDemographicsData(prev => ({ ...prev, [campaignId]: response.data }));
    } catch (err) {
      console.error('Error loading demographics:', err);
      setDemographicsData(prev => ({ ...prev, [campaignId]: null }));
    } finally {
      setDemographicsLoading(prev => ({ ...prev, [campaignId]: false }));
    }
  };

  const toggleDemographics = (campaignId: string) => {
    const isCurrentlyShowingDemographics = demographicsView[campaignId];
    setDemographicsView(prev => ({ ...prev, [campaignId]: !isCurrentlyShowingDemographics }));

    if (!isCurrentlyShowingDemographics) {
      loadDemographics(campaignId);
    }
  };

  // A Sense Max campaign — the cross-platform launcher's output. Detected by the SM_
  // naming (SM_<Platform>_<Tool>_…) or the draft flag. Rendered with its own accent and
  // a slightly different card (no per-campaign demographics toggle — metrics are summed
  // across all platforms it launched to).
  const isSenseMax = (c: Campaign) => !!c.sense_max_draft || !!c.sense_max || (c.name || '').startsWith('SM_');

  // The SM_<Platform>_<Tool>_<Name>_<rand>[_<ts>] name is a SERVICE label on the platform
  // cabinets — in the UI we show only the clean campaign name. Extract the <Name> segment
  // and de-slug it (CamelCase → spaced).
  const displayCampaignName = (c: Campaign): string => {
    const raw = c.name || '';
    if (!raw.startsWith('SM_')) return cleanName(raw);
    const m = raw.match(/^SM_[^_]+_[^_]+_(.+?)(?:_\d{6,})+$/);
    const base = m ? m[1] : raw;
    return base.replace(/([\p{Ll}\p{N}])(\p{Lu})/gu, '$1 $2');
  };

  // A launched Sense Max campaign is more than a single DV campaign — pause/resume must go
  // through the SAME endpoint as its Sense Max dashboard (POST /api/sense-max/campaign/{id}/
  // status), which runs the all-or-none guard (every tool launched + aggregate balance).
  const isLaunchedMax = (c: Campaign) => !!c.sense_max && !c.sense_max_draft;

  // The Sense Max status endpoint can 400 with a STRUCTURED balance detail (object, not a
  // string) — coerce it to a readable line so alert() never shows "[object Object]".
  const errDetail = (err: any): string => {
    const d = err?.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (d && d.code === 'insufficient_balance') {
      const ccy = d.currency || '';
      const m = (v: number) =>
        `${Number(v || 0).toLocaleString(currentLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${ccy}`.trim();
      return t('campaigns.notEnoughBalanceResume', { need: m(d.need), have: m(d.have) });
    }
    return err?.message || t('campaigns.somethingWrong');
  };

  const pauseCampaign = async (campaign: Campaign) => {
    const campaignId = campaign.id;
    if (actionLoading[campaignId]) return;

    setActionLoading(prev => ({ ...prev, [campaignId]: true }));
    setOpenDropdown(null);

    try {
      const ok = isLaunchedMax(campaign)
        ? !!(await api.post(`/api/sense-max/campaign/${campaignId}/status`, { status: 'paused' })).data
        : (await api.post(`/api/ads/campaigns/${campaignId}/pause`)).data?.success;

      if (ok) {
        setCampaigns(prev => prev.map(c =>
          c.id === campaignId ? { ...c, status: 'paused' as const } : c
        ));
        setCounts(prev => ({ ...prev, active: prev.active - 1, paused: prev.paused + 1 }));
      }
    } catch (err: any) {
      console.error('Error pausing campaign:', err);
      alert(t('campaigns.errPausing', { msg: errDetail(err) }));
    } finally {
      setActionLoading(prev => ({ ...prev, [campaignId]: false }));
    }
  };

  const activateCampaign = async (campaign: Campaign) => {
    const campaignId = campaign.id;
    if (actionLoading[campaignId]) return;

    setActionLoading(prev => ({ ...prev, [campaignId]: true }));
    setOpenDropdown(null);

    try {
      // Max → dashboard status endpoint (all-or-none resume guard raises 400 on a partial or
      // under-funded campaign, surfaced by the alert below). Regular → the plain activate.
      const ok = isLaunchedMax(campaign)
        ? !!(await api.post(`/api/sense-max/campaign/${campaignId}/status`, { status: 'active' })).data
        : (await api.post(`/api/ads/campaigns/${campaignId}/activate`)).data?.success;

      if (ok) {
        setCampaigns(prev => prev.map(c =>
          c.id === campaignId ? { ...c, status: 'active' as const } : c
        ));
        setCounts(prev => ({ ...prev, active: prev.active + 1, paused: prev.paused - 1 }));
      }
    } catch (err: any) {
      console.error('Error activating campaign:', err);
      alert(t('campaigns.errActivating', { msg: errDetail(err) }));
    } finally {
      setActionLoading(prev => ({ ...prev, [campaignId]: false }));
    }
  };

  const toggleAdGroupStatus = async (adGroup: AdGroup, newStatus: 'active' | 'paused') => {
    const loadingKey = `ag-${adGroup.id}`;
    if (actionLoading[loadingKey]) return;

    setActionLoading(prev => ({ ...prev, [loadingKey]: true }));
    setOpenDropdown(null);

    try {
      const response = await api.patch(
        `/api/groups/${adGroup.ads_io_id || adGroup.id}`,
        { entity_status: newStatus }
      );

      if (response.data.success) {
        // Update local state
        setAdGroups(prev => prev.map(ag =>
          ag.id === adGroup.id ? { ...ag, status: newStatus } : ag
        ));
      }
    } catch (err: any) {
      console.error('Error toggling ad group status:', err);
      alert(t('campaigns.errGeneric', { msg: err.response?.data?.detail || err.message }));
    } finally {
      setActionLoading(prev => ({ ...prev, [loadingKey]: false }));
    }
  };

  const publishCampaign = async (campaignId: string) => {
    setActionLoading(prev => ({ ...prev, [campaignId]: true }));
    setOpenDropdown(null);
    try {
      const response = await api.post(
        `/api/sense/campaigns/${campaignId}/push`
      );
      if (response.data.success) {
        // Update campaign status from draft to active
        setCampaigns(prev => prev.map(c =>
          c.id === campaignId ? { ...c, status: 'active' as const } : c
        ));
        // Update counts
        setCounts(prev => ({
          ...prev,
          draft: prev.draft - 1,
          active: prev.active + 1
        }));
      }
    } catch (err: any) {
      console.error('Error publishing campaign:', err);
      alert(t('campaigns.errPublishing', { msg: err.response?.data?.detail || err.message }));
    } finally {
      setActionLoading(prev => ({ ...prev, [campaignId]: false }));
    }
  };

  const handleDeleteCampaign = async () => {
    if (!deletingCampaign || isDeleting) return;

    setIsDeleting(true);
    try {
      // Sense Max drafts live in sense_max_drafts (id is "smdraft-<id>") — delete
      // via the Sense Max endpoint, not the campaign one.
      if (deletingCampaign.sense_max_draft) {
        await api.delete(`/api/sense-max/drafts/${deletingCampaign.sense_max_id}`);
        setCampaigns(prev => prev.filter(c => c.id !== deletingCampaign.id));
        setCounts(prev => ({ ...prev, all: prev.all - 1, draft: (prev.draft || 0) - 1 }));
        setShowDeleteModal(false);
        setDeletingCampaign(null);
        return;
      }
      // A LAUNCHED Max campaign is more than its Sense leg — plain archive left the
      // launch jobs, which resurrected the card. Full teardown pauses every tool,
      // archives the Sense leg, and removes the draft + jobs.
      if (isSenseMax(deletingCampaign)) {
        await api.post(`/api/sense-max/campaign/${deletingCampaign.id}/teardown`);
        setCampaigns(prev => prev.filter(c => c.id !== deletingCampaign.id));
        setCounts(prev => ({ ...prev, all: Math.max(0, prev.all - 1) }));
        setShowDeleteModal(false);
        setDeletingCampaign(null);
        return;
      }
      // For published campaigns (not draft), archive instead of delete
      const isDraft = deletingCampaign.status === 'draft';
      const response = isDraft
        ? await api.delete(`/api/sense/campaigns/${deletingCampaign.id}`)
        : await api.post(`/api/sense/campaigns/${deletingCampaign.id}/archive`);

      if (response.data.success) {
        // Remove from campaigns list (archived campaigns are hidden from UI)
        setCampaigns(prev => prev.filter(c => c.id !== deletingCampaign.id));
        // Update counts
        const statusKey = deletingCampaign.status as keyof typeof counts;
        setCounts(prev => ({
          ...prev,
          all: prev.all - 1,
          [statusKey]: (prev[statusKey] || 0) - 1
        }));
        setShowDeleteModal(false);
        setDeletingCampaign(null);
      } else {
        throw new Error(response.data.message
          || t(isDraft ? 'campaigns.failedDeleteCampaign' : 'campaigns.failedArchiveCampaign'));
      }
    } catch (error: any) {
      console.error('Error deleting/archiving campaign:', error);
      const isDraft = deletingCampaign.status === 'draft';
      alert(t(isDraft ? 'campaigns.errDeletingCampaign' : 'campaigns.errArchivingCampaign',
        { msg: error.response?.data?.detail || error.message }));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!deletingGroup || isDeletingGroup) return;

    setIsDeletingGroup(true);
    try {
      // For published groups (has ads_io_id), archive instead of delete
      const isPublished = !!deletingGroup.ads_io_id;
      const response = isPublished
        ? await api.post(`/api/sense/groups/${deletingGroup.id}/archive`)
        : await api.delete(`/api/sense/groups/${deletingGroup.id}`);

      if (response.data.success) {
        // Remove from ad groups list (archived groups are hidden from UI)
        setAdGroups(prev => prev.filter(g => g.id !== deletingGroup.id));
        setShowDeleteGroupModal(false);
        setDeletingGroup(null);
        setOpenDropdown(null);
      } else {
        throw new Error(response.data.message
          || t(isPublished ? 'campaigns.failedArchiveGroup' : 'campaigns.failedDeleteGroup'));
      }
    } catch (error: any) {
      console.error('Error deleting/archiving group:', error);
      const isPublished = !!deletingGroup.ads_io_id;
      alert(t(isPublished ? 'campaigns.errArchivingGroup' : 'campaigns.errDeletingGroup',
        { msg: error.response?.data?.detail || error.message }));
    } finally {
      setIsDeletingGroup(false);
    }
  };

  const handleDuplicateGroup = async (group: AdGroup) => {
    if (isDuplicatingGroup || !slidePanel.campaign) return;

    setIsDuplicatingGroup(true);
    setOpenDropdown(null);
    try {
      const response = await api.post(`/api/sense/groups/${group.id}/duplicate`);

      if (response.data.success) {
        // Reload ad groups list to get fresh data from server
        const adGroupsResponse = await api.get<{ ad_groups: AdGroup[], count: number }>(
          `/api/reports/campaign/${slidePanel.campaign.id}/ad-groups`
        );
        setAdGroups(adGroupsResponse.data.ad_groups);
      } else {
        throw new Error(response.data.message || t('campaigns.dupGroupFailed'));
      }
    } catch (error: any) {
      console.error('Error duplicating group:', error);
      alert(t('campaigns.errDuplicatingGroup', { msg: error.response?.data?.detail || error.message }));
    } finally {
      setIsDuplicatingGroup(false);
    }
  };

  const handleDuplicateCampaign = async (campaign: Campaign) => {
    if (isDuplicatingCampaign) return;

    setIsDuplicatingCampaign(true);
    setOpenDropdown(null);
    try {
      const response = await api.post(`/api/sense/campaigns/${campaign.id}/duplicate`);

      if (response.data.success) {
        // Navigate to the new campaign's edit page
        navigate(`/campaigns/${response.data.id}/edit`);
      } else {
        throw new Error(response.data.message || t('campaigns.dupCampaignFailed'));
      }
    } catch (error: any) {
      console.error('Error duplicating campaign:', error);
      alert(t('campaigns.errDuplicatingCampaign', { msg: error.response?.data?.detail || error.message }));
    } finally {
      setIsDuplicatingCampaign(false);
    }
  };

  const openAdGroupsPanel = async (campaign: Campaign) => {
    setSlidePanel({ open: true, campaign });
    setAdGroupsLoading(true);
    setOpenDropdown(null);

    try {
      const response = await api.get<{ ad_groups: AdGroup[], count: number }>(
        `/api/reports/campaign/${campaign.id}/ad-groups`
      );
      setAdGroups(response.data.ad_groups);
    } catch (err: any) {
      console.error('Error loading ad groups:', err);
      setAdGroups([]);
    } finally {
      setAdGroupsLoading(false);
    }
  };

  const closeAdGroupsPanel = () => {
    setSlidePanel({ open: false, campaign: null });
    setAdGroups([]);
  };

  const handleApplyBudgetRecommendation = async (adGroup: AdGroup) => {
    if (!adGroup.ads_io_id || !adGroup.recommended_budget) return;

    setApplyingBudgetForGroup(adGroup.id);
    try {
      await api.post(`/api/groups/${adGroup.ads_io_id}/apply-budget-recommendation`);
      // Update local state
      setAdGroups(prev => prev.map(g =>
        g.id === adGroup.id ? { ...g, recommended_budget: null, budget: adGroup.recommended_budget || g.budget } : g
      ));
    } catch (error: any) {
      console.error('Error applying budget recommendation:', error);
      alert(t('campaigns.errGeneric', { msg: error.response?.data?.detail || error.message }));
    } finally {
      setApplyingBudgetForGroup(null);
    }
  };

  const filteredCampaigns = useMemo(() => {
    // Aggressive deduplication using Map to guarantee unique IDs
    const campaignMap = new Map<string, Campaign>();
    campaigns.forEach(campaign => {
      if (!campaignMap.has(campaign.id)) {
        campaignMap.set(campaign.id, campaign);
      }
    });
    const uniqueCampaigns = Array.from(campaignMap.values());

    const result = uniqueCampaigns
      .filter(c => {
        const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
        const matchesSearch = !searchQuery ||
          c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          c.advertiser.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesStatus && matchesSearch;
      })
      .sort((a, b) => {
        // Pacing priority: danger (0) → ok (1) → paused (2)
        const getPacingOrder = (c: Campaign): number => {
          const hasBehind = ((c.settings_issue_count ?? 0) > 0) || ((c.budget_needed_count ?? 0) > 0);
          const hasMediaPlan = (c.media_plan_groups_count ?? 0) > 0;
          const allPaused = hasMediaPlan && !hasBehind && (c.daily_paused_count ?? 0) > 0 &&
            (c.daily_paused_count ?? 0) === (c.media_plan_groups_count ?? 0);

          if (hasBehind) return 0; // danger first
          if (allPaused) return 2; // paused last
          return 1; // ok (everything else) in the middle
        };

        const pacingA = getPacingOrder(a);
        const pacingB = getPacingOrder(b);
        if (pacingA !== pacingB) return pacingA - pacingB;

        // For ended campaigns: sort by end_date descending (most recent first)
        if (a.status === 'ended' && b.status === 'ended') {
          const endA = a.end_date ? new Date(a.end_date).getTime() : 0;
          const endB = b.end_date ? new Date(b.end_date).getTime() : 0;
          return endB - endA; // descending
        }
        // Otherwise: active first, then paused, then draft, then ended
        const order: Record<string, number> = { active: 0, paused: 1, draft: 2, ended: 3 };
        return (order[a.status] ?? 99) - (order[b.status] ?? 99);
      });

    return result;
  }, [campaigns, statusFilter, searchQuery]);

  // Mini sparkline for ad groups table
  const AdGroupSparkline: React.FC<{ data: AdGroupTrendPoint[], adGroupId: string }> = ({ data, adGroupId }) => {
    const [tooltip, setTooltip] = useState<{ x: number; index: number; date: string; value: number; change: number | null } | null>(null);

    if (!data || data.length === 0) {
      return <div className="chart-cell empty">—</div>;
    }

    const width = 120;
    const height = 40;
    const padding = 4;

    const getValue = (d: AdGroupTrendPoint) => d.impressions;
    const values = data.map(getValue);
    const max = Math.max(...values);
    const min = Math.min(...values);
    const range = max - min || 1;

    const getX = (index: number) => padding + (index / Math.max(data.length - 1, 1)) * (width - padding * 2);
    const getY = (value: number) => height - padding - ((value - min) / range) * (height - padding * 2);

    // Create smooth bezier curve (Catmull-Rom to Bezier)
    const createSmoothPath = () => {
      if (data.length < 2) {
        const x = getX(0);
        const y = getY(getValue(data[0]));
        return `M ${x} ${y}`;
      }

      const pts = data.map((d, i) => ({ x: getX(i), y: getY(getValue(d)) }));

      if (pts.length === 2) {
        return `M ${pts[0].x} ${pts[0].y} L ${pts[1].x} ${pts[1].y}`;
      }

      let path = `M ${pts[0].x} ${pts[0].y}`;

      for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[Math.max(0, i - 1)];
        const p1 = pts[i];
        const p2 = pts[i + 1];
        const p3 = pts[Math.min(pts.length - 1, i + 2)];

        const tension = 6;
        const cp1x = p1.x + (p2.x - p0.x) / tension;
        const cp1y = p1.y + (p2.y - p0.y) / tension;
        const cp2x = p2.x - (p3.x - p1.x) / tension;
        const cp2y = p2.y - (p3.y - p1.y) / tension;

        path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
      }

      return path;
    };

    const smoothPath = createSmoothPath();

    const isPositive = data.length >= 2 && getValue(data[data.length - 1]) >= getValue(data[0]);
    const _dievoSpark = getBranding().edition !== 'sense';
    const strokeColor = isPositive ? (_dievoSpark ? '#3E5310' : '#2ecc71') : '#FF6B4A';
    const fillColor = isPositive && _dievoSpark ? '#C8F032' : strokeColor;
    const gradientId = `ag-gradient-${adGroupId}`;

    // Area fill path (smooth curve closed at bottom)
    const areaPath = `${smoothPath} L ${getX(data.length - 1)} ${height - padding} L ${padding} ${height - padding} Z`;

    const lastIndex = data.length - 1;
    const lastX = getX(lastIndex);
    const lastY = getY(getValue(data[lastIndex]));

    const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const index = Math.round(((x - padding) / (width - padding * 2)) * (data.length - 1));
      const clampedIndex = Math.max(0, Math.min(data.length - 1, index));
      const point = data[clampedIndex];
      if (point) {
        let change: number | null = null;
        if (clampedIndex > 0) {
          const prevValue = getValue(data[clampedIndex - 1]);
          if (prevValue > 0) {
            change = ((getValue(point) - prevValue) / prevValue) * 100;
          }
        }
        setTooltip({ x: getX(clampedIndex), index: clampedIndex, date: point.date, value: getValue(point), change });
      }
    };

    const handleMouseLeave = () => setTooltip(null);

    const formatTooltipDate = (dateStr: string) => {
      const date = new Date(dateStr);
      return date.toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' });
    };

    return (
      <div className="chart-cell">
        <svg
          width={width}
          height={height}
          className="mini-chart"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={fillColor} stopOpacity="0.3" />
              <stop offset="100%" stopColor={fillColor} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={areaPath} fill={`url(#${gradientId})`} />
          <path
            d={smoothPath}
            fill="none"
            stroke={strokeColor}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx={lastX} cy={lastY} r="4" fill={strokeColor} />
          {tooltip && tooltip.index !== lastIndex && (
            <>
              <circle cx={tooltip.x} cy={getY(tooltip.value)} r="4" fill={strokeColor} />
              <line
                x1={tooltip.x}
                y1={padding}
                x2={tooltip.x}
                y2={height - padding}
                stroke={strokeColor}
                strokeWidth="1"
                strokeDasharray="2,2"
                opacity="0.5"
              />
            </>
          )}
        </svg>
        {tooltip && (
          <div className="chart-tooltip show" style={{ left: tooltip.x, top: -8 }}>
            <div className="chart-tooltip-date">{formatTooltipDate(tooltip.date)}</div>
            <div className="chart-tooltip-value">{formatNumber(tooltip.value)} {t('campaigns.imprShortUnit')}</div>
            {tooltip.change !== null && (
              <div className={`chart-tooltip-change ${tooltip.change >= 0 ? 'positive' : 'negative'}`}>
                {tooltip.change >= 0 ? '+' : ''}{tooltip.change.toFixed(1)}%
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Sparkline for card view
  const CardSparkline: React.FC<{ data: TrendPoint[], campaignId: string, metric: MetricType, budgetCurrency?: string }> = ({ data, campaignId, metric, budgetCurrency }) => {
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
    const svgRef = React.useRef<SVGSVGElement>(null);

    const metricLabels: Record<MetricType, string> = {
      impressions: t('campaigns.unitImpressions'),
      clicks: t('campaigns.unitClicks'),
      ctr: '% CTR',
      spend: t('campaigns.unitSpend'),
      conversions: t('campaigns.unitConversions'),
      cpc: 'CPC',
      cpm: 'CPM',
      cpa: 'CPA'
    };

    if (!data || data.length === 0) {
      return <div className="card-chart-empty">{t('campaigns.noData')}</div>;
    }

    const width = 400;
    const height = 80;
    const padding = 10;

    // Cost metrics are ratios, so they're recomputed per day rather than averaged —
    // a day with no clicks/impressions has no defined cost and reads as 0.
    const getValue = (d: TrendPoint): number => {
      if (metric === 'cpc') return d.clicks > 0 ? d.spend / d.clicks : 0;
      if (metric === 'cpm') return d.impressions > 0 ? (d.spend / d.impressions) * 1000 : 0;
      if (metric === 'cpa') return d.conversions > 0 ? d.spend / d.conversions : 0;
      return d[metric];
    };
    const values = data.map(getValue);
    const max = Math.max(...values);
    const min = Math.min(...values);
    const range = max - min || 1;

    const getX = (index: number) => padding + (index / Math.max(data.length - 1, 1)) * (width - padding * 2);
    const getY = (value: number) => height - padding - ((value - min) / range) * (height - padding * 2);

    // Create smooth bezier curve that passes through all points (Catmull-Rom to Bezier)
    const createSmoothPath = () => {
      if (data.length < 2) {
        const x = getX(0);
        const y = getY(getValue(data[0]));
        return `M ${x} ${y}`;
      }

      const pts = data.map((d, i) => ({ x: getX(i), y: getY(getValue(d)) }));

      if (pts.length === 2) {
        return `M ${pts[0].x} ${pts[0].y} L ${pts[1].x} ${pts[1].y}`;
      }

      let path = `M ${pts[0].x} ${pts[0].y}`;

      for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[Math.max(0, i - 1)];
        const p1 = pts[i];
        const p2 = pts[i + 1];
        const p3 = pts[Math.min(pts.length - 1, i + 2)];

        // Catmull-Rom to Cubic Bezier conversion
        const tension = 6; // Higher = less smooth, lower = more smooth
        const cp1x = p1.x + (p2.x - p0.x) / tension;
        const cp1y = p1.y + (p2.y - p0.y) / tension;
        const cp2x = p2.x - (p3.x - p1.x) / tension;
        const cp2y = p2.y - (p3.y - p1.y) / tension;

        path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
      }

      return path;
    };

    const smoothPath = createSmoothPath();

    // Determine trend: compare second-to-last day with first day
    const isPositive = data.length >= 2 && getValue(data[data.length - 2]) >= getValue(data[0]);
    const _dievoSpark = getBranding().edition !== 'sense';
    // Dievo positive trend matches the Reports chart: dark-olive line, lime fill.
    const strokeColor = isPositive ? (_dievoSpark ? '#3E5310' : '#2ecc71') : '#FF6B4A';
    const strokeColorEnd = isPositive ? (_dievoSpark ? '#3E5310' : '#27ae60') : '#E94B6C';
    const fillColor = isPositive && _dievoSpark ? '#C8F032' : strokeColor;
    const gradientId = `card-gradient-${campaignId}-${metric}`;

    // Area fill path (smooth curve closed at bottom)
    const areaPath = `${smoothPath} L ${getX(data.length - 1)} ${height - padding} L ${padding} ${height - padding} Z`;

    // Last point coordinates
    const lastIndex = data.length - 1;
    const lastX = getX(lastIndex);
    const lastY = getY(getValue(data[lastIndex]));

    const formatTooltipDate = (dateStr: string) => {
      const date = new Date(dateStr);
      return date.toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' });
    };

    const formatTooltipValue = (value: number) => {
      if (metric === 'spend' || metric === 'cpc' || metric === 'cpm' || metric === 'cpa') return formatCurrency(value, budgetCurrency || currency);
      if (metric === 'ctr') return value.toFixed(2) + '%';
      if (metric === 'conversions') return value.toLocaleString(currentLocale());
      return formatNumber(value);
    };

    const getChange = (index: number) => {
      if (index === 0) return null;
      const prev = getValue(data[index - 1]);
      const curr = getValue(data[index]);
      if (prev === 0) return null;
      const change = ((curr - prev) / prev) * 100;
      return change;
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!svgRef.current) return;
      const rect = svgRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentX = x / rect.width;

      // Find closest data point based on percentage position
      const closestIndex = Math.round(percentX * (data.length - 1));
      const clampedIndex = Math.max(0, Math.min(data.length - 1, closestIndex));

      // Calculate pixel positions for tooltip and dot
      const dotX = (getX(clampedIndex) / width) * rect.width;
      const dotY = (getY(getValue(data[clampedIndex])) / height) * rect.height;

      setHoveredIndex(clampedIndex);
      setTooltipPos({ x: dotX, y: dotY });
    };

    const handleMouseLeave = () => {
      setHoveredIndex(null);
    };

    // Calculate end dot position in percentages
    const endDotLeft = (lastX / width) * 100;
    const endDotTop = (lastY / height) * 100;

    return (
      <div
        className="card-chart-wrapper"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <svg
          ref={svgRef}
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
          className="card-sparkline-svg"
        >
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
              {isPositive && _dievoSpark ? (
                <>
                  <stop offset="0%" stopColor="#C8F032" stopOpacity="0.55" />
                  <stop offset="60%" stopColor="#C8F032" stopOpacity="0.22" />
                  <stop offset="100%" stopColor="#C8F032" stopOpacity="0.04" />
                </>
              ) : (
                <>
                  <stop offset="0%" stopColor={fillColor} stopOpacity="0.2" />
                  <stop offset="100%" stopColor={fillColor} stopOpacity="0" />
                </>
              )}
            </linearGradient>
            <linearGradient id={`${gradientId}-stroke`} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={strokeColor} />
              <stop offset="100%" stopColor={strokeColorEnd} />
            </linearGradient>
          </defs>
          <path d={areaPath} fill={`url(#${gradientId})`} />
          <path
            d={smoothPath}
            fill="none"
            stroke={`url(#${gradientId}-stroke)`}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>

        {/* HTML Hover dot */}
        {hoveredIndex !== null && (
          <div
            className="chart-dot hover-dot"
            style={{
              left: tooltipPos.x,
              top: tooltipPos.y,
              backgroundColor: strokeColor
            }}
          >
            <div className="chart-dot-inner"></div>
          </div>
        )}

        {/* HTML End dot */}
        {hoveredIndex !== lastIndex && (
          <div
            className="chart-dot end-dot"
            style={{
              left: `${endDotLeft}%`,
              top: `${endDotTop}%`,
              backgroundColor: strokeColorEnd
            }}
          />
        )}

        {/* HTML Tooltip */}
        {hoveredIndex !== null && (
          <div
            className="chart-tooltip show"
            style={{
              left: tooltipPos.x,
              top: tooltipPos.y - 60
            }}
          >
            <div className="chart-tooltip-date">{formatTooltipDate(data[hoveredIndex].date)}</div>
            <div className="chart-tooltip-value">{formatTooltipValue(getValue(data[hoveredIndex]))} {metricLabels[metric]}</div>
            {getChange(hoveredIndex) !== null && (
              <div className={`chart-tooltip-change ${getChange(hoveredIndex)! >= 0 ? 'positive' : 'negative'}`}>
                {getChange(hoveredIndex)! >= 0 ? '+' : ''}{getChange(hoveredIndex)!.toFixed(1)}% {t('campaigns.vsPrevDay')}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderTypeIndicator = (type: string) => {
    if (type === 'display') {
      return (
        <div className="type-indicator display" title={t('campaigns.display')} key="display">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H3V5h18v14z"/>
          </svg>
        </div>
      );
    }
    if (type === 'video') {
      return (
        <div className="type-indicator video" title={t('campaigns.video')} key="video">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/>
          </svg>
        </div>
      );
    }
    if (type === 'audio') {
      return (
        <div className="type-indicator audio" title={t('campaigns.audio')} key="audio">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
          </svg>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      {/* Header */}
      <header className="header">
        <div className="header-title">
          <h1>{t('campaigns.title')}</h1>
          <p className="header-subtitle">{t('campaigns.subtitle')}</p>
        </div>
        <div className="header-actions">
          {!isAnalyst && <NewCampaignButton />}
          {!isAnalyst && !isAllClients && selectedAdvertiser?.has_external_integration && <SenseMaxButton />}
        </div>
      </header>

      {/* Filters Bar */}
      <div className="filters-bar">
        <div className="filters-left">
          <div className="status-segmented">
            <button
              className={`status-segment ${statusFilter === 'all' ? 'active' : ''}`}
              onClick={() => setStatusFilter('all')}
            >
              All <span className="segment-count">{counts.all}</span>
            </button>
            <button
              className={`status-segment ${statusFilter === 'active' ? 'active' : ''}`}
              onClick={() => setStatusFilter('active')}
            >
              {t('campaigns.live')} <span className="segment-count">{counts.active}</span>
            </button>
            <button
              className={`status-segment ${statusFilter === 'paused' ? 'active' : ''}`}
              onClick={() => setStatusFilter('paused')}
            >
              {t('campaigns.paused')} <span className="segment-count">{counts.paused}</span>
            </button>
{counts.draft > 0 && (
              <button
                className={`status-segment ${statusFilter === 'draft' ? 'active' : ''}`}
                onClick={() => setStatusFilter('draft')}
              >
                {t('campaigns.draft')} <span className="segment-count">{counts.draft}</span>
              </button>
            )}
            <button
              className={`status-segment ${statusFilter === 'ended' ? 'active' : ''}`}
              onClick={() => setStatusFilter('ended')}
            >
              {t('campaigns.ended')} <span className="segment-count">{counts.ended}</span>
            </button>
          </div>
        </div>
        <div className="filters-right">
          <div className="search-box">
            <svg className="search-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
            </svg>
            <input
              type="text"
              className="search-input"
              placeholder={t('campaigns.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="error-message">{error}</div>
      )}

      {/* Loading Skeleton */}
      {loading && (
        <div className="campaigns-cards">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="campaign-card skeleton">
              <div className="card-header">
                <div className="campaign-name-card">
                  <div className="skeleton-line skeleton-type"></div>
                  <div>
                    <div className="skeleton-line skeleton-title"></div>
                    <div className="skeleton-line skeleton-subtitle"></div>
                  </div>
                </div>
                <div className="skeleton-line skeleton-badge"></div>
              </div>
              <div className="skeleton-line skeleton-dates"></div>
              <div className="skeleton-line skeleton-chart"></div>
              <div className="card-metrics-grid">
                <div className="metric-box">
                  <div className="skeleton-line skeleton-metric-label"></div>
                  <div className="skeleton-line skeleton-metric-value"></div>
                </div>
                <div className="metric-box">
                  <div className="skeleton-line skeleton-metric-label"></div>
                  <div className="skeleton-line skeleton-metric-value"></div>
                </div>
                <div className="metric-box">
                  <div className="skeleton-line skeleton-metric-label"></div>
                  <div className="skeleton-line skeleton-metric-value"></div>
                </div>
                <div className="metric-box">
                  <div className="skeleton-line skeleton-metric-label"></div>
                  <div className="skeleton-line skeleton-metric-value"></div>
                </div>
              </div>
              <div className="skeleton-line skeleton-costs"></div>
              <div className="skeleton-line skeleton-budget"></div>
            </div>
          ))}
        </div>
      )}

      {/* Campaigns Cards View */}
      {!loading && !error && (
        <div className="campaigns-cards">
          {filteredCampaigns.map((campaign) => {
            // Sense Max cards show SUMMED cross-platform metrics (Sense leg + external).
            const _bd = smBudgetMap[campaign.id];
            const _isMax = isSenseMax(campaign);
            // Max card metrics need the budget breakdown to be summed; until it arrives, hide the
            // numbers (CSS) so they don't flash the Sense-only value then jump to the total.
            const _bdPending = _isMax && smBudgetLoading && !_bd;
            const mImpr = _isMax && _bd ? campaign.impressions + (_bd.external_impressions || 0) : campaign.impressions;
            const mClicks = _isMax && _bd ? campaign.clicks + (_bd.external_clicks || 0) : campaign.clicks;
            const mSpend = _isMax && _bd ? campaign.spend + (_bd.external_spend || 0) : campaign.spend;
            const mCtr = _isMax && _bd ? (mImpr > 0 ? mClicks / mImpr * 100 : 0) : campaign.ctr;
            const _ak = _isMax && _bd ? (_bd.commission || 0) : 0;
            // Sparkline: SUM the Sense daily (campaign.trend) with the external daily (_bd.trend)
            // by date — otherwise a Max card charts only the external leg (Meta) and drops Sense.
            const _trend = _isMax ? mergeTrendByDate(campaign.trend, _bd?.trend) : campaign.trend;

            // Budget completion: for Max = (Sense + External + Commission) / campaign budget.
            // For Max the denominator is the FULL client budget (total_budget from /budget) —
            // NOT campaign.budget, which is only the Sense leg's 35% share and would inflate the %.
            const budgetBase = _isMax ? (mSpend + _ak) : campaign.spend;
            const _budgetTotal = _isMax && _bd ? (_bd.total_budget ?? campaign.budget) : campaign.budget;
            const budgetPercent = _budgetTotal && _budgetTotal > 0
              ? (budgetBase / _budgetTotal) * 100
              : 0;
            const isOverBudget = budgetPercent >= 90;

            return (
              <div key={campaign.id} className={`campaign-card ${openDropdown === campaign.id ? 'menu-active' : ''} ${actionLoading[campaign.id] ? 'action-loading' : ''} ${campaign.has_draft_groups ? 'has-unpublished' : ''} ${isSenseMax(campaign) ? 'sense-max' : ''} ${_bdPending ? 'metrics-loading' : ''}`}>
                {campaign.has_draft_groups && (
                  <div className="corner-ribbon">{t('campaigns.unpublished')}</div>
                )}
                {actionLoading[campaign.id] && (
                  <div className="card-loading-overlay">
                    <div className="card-loading-spinner"></div>
                    <span>{t('campaigns.updating')}</span>
                  </div>
                )}
                <div className="card-header">
                  <div className="campaign-name-card">
                    {isSenseMax(campaign)
                      ? <div className="type-indicator sense-max-m" title={t('campaigns.maxCampaign')} key="sm-m">M</div>
                      : campaign.types.map(type => renderTypeIndicator(type))}
                    <div className="campaign-name-text">
                      <div className="card-title" title={displayCampaignName(campaign)} style={campaign.is_locked ? { color: '#888' } : undefined}>
                        {campaign.is_locked && (
                          <span title={t('campaigns.legacyReadOnly')} style={{ marginRight: 6, color: '#888' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ verticalAlign: 'middle' }}>
                              <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6zm9 14H6V10h12v10zm-6-3c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2z"/>
                            </svg>
                          </span>
                        )}
                        {displayCampaignName(campaign)}
                      </div>
                      <div className="card-subtitle">
                        {isSenseMax(campaign) ? (
                          <>{campaign.advertiser}</>
                        ) : (
                          <>{campaign.advertiser} • <span className="clickable-subtitle" onClick={() => openAdGroupsPanel(campaign)}>{t('campaigns.adGroupsCount', { count: campaign.io_count })}</span></>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="card-actions">
                    <span className={`status-badge ${campaign.status === 'active' ? 'live' : campaign.status}`}>
                      {campaign.status === 'active' && <span className="status-dot live"></span>}
                      {campaign.status === 'paused' && (
                        <svg className="status-badge-icon" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                        </svg>
                      )}
                      {campaign.status === 'ended' && (
                        <svg className="status-badge-icon" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                        </svg>
                      )}
                      {campaign.status === 'draft' && (
                        <svg className="status-badge-icon" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                        </svg>
                      )}
                      {campaign.status === 'active' ? t('campaigns.live') : campaign.status === 'paused' ? t('campaigns.paused') : campaign.status === 'draft' ? t('campaigns.draft') : t('campaigns.ended')}
                    </span>
                    {(() => {
                      const hasBehind = ((campaign.settings_issue_count ?? 0) > 0) || ((campaign.budget_needed_count ?? 0) > 0);
                      const allPaused = !hasBehind && (campaign.daily_paused_count ?? 0) > 0 &&
                        (campaign.daily_paused_count ?? 0) === (campaign.media_plan_groups_count ?? 0);

                      if (hasBehind) {
                        return (
                          <span
                            className="campaign-alert-badge danger"
                            title={`${(campaign.settings_issue_count ?? 0) + (campaign.budget_needed_count ?? 0)} group(s) behind pace`}
                            onClick={() => openAdGroupsPanel(campaign)}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                            </svg>
                            {(campaign.settings_issue_count ?? 0) + (campaign.budget_needed_count ?? 0)}
                          </span>
                        );
                      } else if (allPaused) {
                        return (
                          <span
                            className="campaign-alert-badge paused"
                            title={`${campaign.daily_paused_count} group(s) paused for today`}
                            onClick={() => openAdGroupsPanel(campaign)}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                              <rect x="6" y="4" width="4" height="16"/>
                              <rect x="14" y="4" width="4" height="16"/>
                            </svg>
                            {campaign.daily_paused_count}
                          </span>
                        );
                      }
                      return null;
                    })()}
                    {campaign.bg_pacing_state === 'THROTTLE' && (
                      <span
                        className="campaign-alert-badge throttle"
                        title={t('campaigns.throttleHint')}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                        </svg>
                        {t('campaigns.throttle')}
                      </span>
                    )}
                    {campaign.bg_pacing_state === 'PAUSED' && (
                      <span
                        className="campaign-alert-badge danger"
                        title={t('campaigns.haltHint')}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14H8V8h2v8zm6 0h-2V8h2v8z"/>
                        </svg>
                        {t('campaigns.halt')}
                      </span>
                    )}
                    <div className={`actions-dropdown ${openDropdown === campaign.id ? 'open' : ''}`}>
                      <button
                        className="actions-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenDropdown(openDropdown === campaign.id ? null : campaign.id);
                        }}
                      >
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                        </svg>
                      </button>
                      <div className="actions-menu">
                        {!isSenseMax(campaign) && (
                        <button className="action-item" onClick={() => openAdGroupsPanel(campaign)}>
                          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z"/></svg>
                          {t('campaigns.viewAdGroups')}
                        </button>
                        )}
                        {!isAnalyst && !campaign.is_locked && (<>
                        {!isSenseMax(campaign) && <div className="action-divider"></div>}
                        <button className="action-item" onClick={() => {
                          setOpenDropdown(null);
                          if (campaign.sense_max_draft) {
                            // A NOT-yet-launched Max draft has no dashboard — open the constructor to edit it.
                            navigate(`/sense-max?draft=${campaign.sense_max_id}`);
                          } else if (isSenseMax(campaign)) {
                            // Launched Max campaigns open a dedicated dashboard
                            // (settings + cross-platform stats + optimization),
                            // not the single-platform campaign editor.
                            navigate(`/campaigns/${campaign.id}/dashboard`, { state: { campaign } });
                          } else {
                            navigate(`/campaigns/${campaign.id}/edit`);
                          }
                        }}>
                          {isSenseMax(campaign) && !campaign.sense_max_draft ? (
                            <>
                              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/></svg>
                              {t('campaigns.dashboard')}
                            </>
                          ) : (
                            <>
                              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>
                              {campaign.sense_max_draft ? t('campaigns.edit') : t('campaigns.editCampaign')}
                            </>
                          )}
                        </button>
                        {/* Duplicate isn't wired for Sense Max campaigns yet — hide it there. */}
                        {!isSenseMax(campaign) && (
                          <button
                            className="action-item"
                            onClick={() => handleDuplicateCampaign(campaign)}
                            disabled={isDuplicatingCampaign}
                          >
                            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/></svg>
                            {isDuplicatingCampaign ? t('campaigns.duplicating') : t('campaigns.duplicate')}
                          </button>
                        )}
                        <div className="action-divider"></div>
                        {campaign.status === 'active' && (
                          <button
                            className={`action-item ${actionLoading[campaign.id] ? 'loading' : ''}`}
                            onClick={() => pauseCampaign(campaign)}
                            disabled={actionLoading[campaign.id]}
                          >
                            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
                            {actionLoading[campaign.id] ? t('campaigns.pausing') : t('campaigns.pauseCampaign')}
                          </button>
                        )}
                        {campaign.status === 'paused' && (
                          <button
                            className={`action-item resume ${actionLoading[campaign.id] ? 'loading' : ''}`}
                            onClick={() => activateCampaign(campaign)}
                            disabled={actionLoading[campaign.id]}
                          >
                            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                            {actionLoading[campaign.id] ? t('campaigns.activating') : t('campaigns.resumeCampaign')}
                          </button>
                        )}
                        <button
                          className="action-item danger"
                          onClick={() => {
                            setOpenDropdown(null);
                            setDeletingCampaign(campaign);
                            setShowDeleteModal(true);
                          }}
                        >
                          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
                          {campaign.status === 'draft' ? t('campaigns.delete') : t('campaigns.archive')}
                        </button>
                        </>)}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-dates-row">
                  <div className="card-dates">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10z"/>
                    </svg>
                    {formatDate(campaign.start_date)} - {formatDate(campaign.end_date)}
                  </div>
                  {campaign.status !== 'draft' && !isSenseMax(campaign) && (
                  <button
                    className="demographics-link"
                    onClick={() => toggleDemographics(campaign.id)}
                  >
                    {demographicsView[campaign.id] ? (
                      <>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M3.5 18.49l6-6.01 4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 16.99z"/>
                        </svg>
                        {t('campaigns.mediaMetrics')}
                      </>
                    ) : (
                      <>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                        </svg>
                        {t('campaigns.demographics')}
                      </>
                    )}
                  </button>
                  )}
                </div>

                {campaign.status === 'draft' ? (
                  /* Draft campaign - simplified view without stats */
                  <div className="draft-card-content">
                    <div className="draft-notice">
                      <svg viewBox="0 0 24 24" fill="currentColor" className="draft-icon">
                        <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                      </svg>
                      <div className="draft-text">
                        <div className="draft-title">{campaign.sense_max_draft ? t('campaigns.maxDraft') : t('campaigns.draftCampaign')}</div>
                        <div className="draft-description">{t(campaign.sense_max_draft ? 'campaigns.maxDraftHint' : 'campaigns.draftNotPublishedHint')}</div>
                      </div>
                    </div>
                    <div className="draft-actions">
                      <button className="btn-edit-draft" onClick={() => navigate(campaign.sense_max_draft ? `/sense-max?draft=${campaign.sense_max_id}` : `/campaigns/${campaign.id}/edit`)}>
                        <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                        </svg>
                        {t('campaigns.edit')}
                      </button>
                      {!campaign.sense_max_draft && (
                      <button
                        className={`btn-publish-draft ${actionLoading[campaign.id] ? 'loading' : ''}`}
                        onClick={() => publishCampaign(campaign.id)}
                        disabled={actionLoading[campaign.id]}
                      >
                        {actionLoading[campaign.id] ? (
                          <>
                            <span className="btn-spinner"></span>
                            {t('campaigns.publishing')}
                          </>
                        ) : (
                          <>
                            <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                            </svg>
                            {t('campaigns.publish')}
                          </>
                        )}
                      </button>
                      )}
                    </div>
                  </div>
                ) : !demographicsView[campaign.id] ? (
                  <>
                <div className="card-chart">
                  <CardSparkline data={_trend} campaignId={`card-${campaign.id}`} metric={getSelectedMetric(campaign.id)} budgetCurrency={campaign.budget_currency || undefined} />
                </div>

                <div className="card-metrics-grid">
                  <div
                    className={`metric-box clickable ${getSelectedMetric(campaign.id) === 'impressions' ? 'selected' : ''}`}
                    onClick={() => setSelectedMetric(campaign.id, 'impressions')}
                  >
                    <div className="metric-label">{t('campaigns.impressions')}</div>
                    <div className="metric-big">{formatNumber(mImpr)}</div>
                    {campaign.status !== 'ended' && campaign.impressions_change !== undefined && campaign.impressions_change !== null && (
                      <div className={`metric-change ${campaign.impressions_change >= 0 ? 'positive' : 'negative'}`}>
                        {campaign.impressions_change >= 0 ? '+' : ''}{campaign.impressions_change.toFixed(0)}%
                      </div>
                    )}
                  </div>
                  <div
                    className={`metric-box clickable ${getSelectedMetric(campaign.id) === 'clicks' ? 'selected' : ''}`}
                    onClick={() => setSelectedMetric(campaign.id, 'clicks')}
                  >
                    <div className="metric-label">{t('campaigns.clicks')}</div>
                    <div className="metric-big">{mClicks.toLocaleString(currentLocale())}</div>
                    {campaign.status !== 'ended' && campaign.clicks_change !== undefined && campaign.clicks_change !== null && (
                      <div className={`metric-change ${campaign.clicks_change >= 0 ? 'positive' : 'negative'}`}>
                        {campaign.clicks_change >= 0 ? '+' : ''}{campaign.clicks_change.toFixed(0)}%
                      </div>
                    )}
                  </div>
                  {campaign.conversions > 0 ? (
                    <div
                      className={`metric-box clickable ${getSelectedMetric(campaign.id) === 'conversions' ? 'selected' : ''}`}
                      onClick={() => setSelectedMetric(campaign.id, 'conversions')}
                    >
                      <div className="metric-label">{t('campaigns.conversions')}</div>
                      <div className="metric-big">{campaign.conversions.toLocaleString(currentLocale())}</div>
                      {campaign.top_conversions && campaign.top_conversions.length > 0 && (
                        <div className="metric-top-conv">
                          {campaign.top_conversions.slice(0, 2).map((g, i) => (
                            <div className="top-conv-row" key={i}>
                              <span className="top-conv-name" title={g.name}>{g.name}</span>
                              <span className="top-conv-count">{g.count.toLocaleString(currentLocale())}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      className={`metric-box clickable ${getSelectedMetric(campaign.id) === 'ctr' ? 'selected' : ''}`}
                      onClick={() => setSelectedMetric(campaign.id, 'ctr')}
                    >
                      <div className="metric-label">CTR</div>
                      <div className="metric-big">{mCtr.toFixed(2)}%</div>
                      {campaign.status !== 'ended' && campaign.ctr_change !== undefined && campaign.ctr_change !== null && (
                        <div className={`metric-change ${campaign.ctr_change >= 0 ? 'positive' : 'negative'}`}>
                          {campaign.ctr_change >= 0 ? '+' : ''}{campaign.ctr_change.toFixed(2)}%
                        </div>
                      )}
                    </div>
                  )}
                  <div
                    className={`metric-box clickable ${getSelectedMetric(campaign.id) === 'spend' ? 'selected' : ''}`}
                    onClick={() => setSelectedMetric(campaign.id, 'spend')}
                  >
                    <div className="metric-label">{t('campaigns.spend')}</div>
                    {/* SPEND = total cost to the client: media (Sense + external) + commission (АК).
                        CPC/CPM below stay media-only. */}
                    <div className="metric-big"><CurrencyValue value={_isMax ? mSpend + _ak : mSpend} currency={campaign.budget_currency || currency} short /></div>
                    {campaign.status !== 'ended' && campaign.spend_change !== undefined && campaign.spend_change !== null && (
                      <div className={`metric-change neutral`}>
                        {campaign.spend_change >= 0 ? '+' : ''}{campaign.spend_change.toFixed(0)}%
                      </div>
                    )}
                  </div>
                </div>

                <div className="card-costs">
                  <div
                    className={`cost-box ${mClicks > 0 ? 'clickable' : ''} ${getSelectedMetric(campaign.id) === 'cpc' ? 'selected' : ''}`}
                    onClick={mClicks > 0 ? () => setSelectedMetric(campaign.id, 'cpc') : undefined}
                  >
                    <span className="cost-label">CPC</span>
                    <span className="cost-value">
                      {mClicks > 0 ? formatCurrency(mSpend / mClicks, campaign.budget_currency || currency) : '—'}
                    </span>
                  </div>
                  <div
                    className={`cost-box ${mImpr > 0 ? 'clickable' : ''} ${getSelectedMetric(campaign.id) === 'cpm' ? 'selected' : ''}`}
                    onClick={mImpr > 0 ? () => setSelectedMetric(campaign.id, 'cpm') : undefined}
                  >
                    <span className="cost-label">CPM</span>
                    <span className="cost-value">
                      {mImpr > 0 ? formatCurrency((mSpend / mImpr) * 1000, campaign.budget_currency || currency) : '—'}
                    </span>
                  </div>
                  {campaign.conversions > 0 ? (
                    <div
                      className={`cost-box clickable ${getSelectedMetric(campaign.id) === 'cpa' ? 'selected' : ''}`}
                      onClick={() => setSelectedMetric(campaign.id, 'cpa')}
                      title={t('campaigns.cpaTooltip')}
                    >
                      <span className="cost-label">CPA</span>
                      <span className="cost-value">
                        {formatCurrency(mSpend / campaign.conversions, campaign.budget_currency || currency)}
                      </span>
                    </div>
                  ) : (
                    <div className={`cost-box highlight ${isOverBudget ? 'warning' : ''}`}>
                      <span className="cost-label">{t('campaigns.budget')}</span>
                      <span className="cost-value">{budgetPercent.toFixed(0)}%</span>
                    </div>
                  )}
                </div>

                {isSenseMax(campaign) ? (() => {
                  const bd = smBudgetMap[campaign.id];
                  const ccy = campaign.budget_currency || currency;
                  const sense = campaign.spend || 0;
                  const ak = bd?.commission || 0;
                  const ext = bd?.external_spend || 0;
                  const own = bd?.model_type === 'own_accounts';
                  const chargedTotal = sense + ak + (own ? ext : 0);
                  return (
                    <SmBudgetStrip sense={sense} ak={ak} ext={ext} chargedTotal={chargedTotal} own={own} ccy={ccy} />
                  );
                })() : (
                <div className={`card-budget ${isOverBudget ? 'warning' : ''}`}>
                  <div className="budget-header">
                    <span className="budget-label">{campaign.has_external_data ? t('campaigns.spend') : t('campaigns.budget')}</span>
                    <span className="budget-amount">
                      {isOverBudget && !campaign.has_external_data && (
                        <span className="budget-warning-icon" title={t('campaigns.budgetLow')}>
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                          </svg>
                        </span>
                      )}
                      {campaign.has_external_data
                        ? <CurrencyValue value={campaign.spend} currency={campaign.budget_currency || currency} />
                        : <>
                            <CurrencyValue value={campaign.spend} currency={campaign.budget_currency || currency} /> / {campaign.budget ? <CurrencyValue value={campaign.budget} currency={campaign.budget_currency || currency} /> : '—'}
                          </>}
                    </span>
                  </div>
                  {!campaign.has_external_data && (
                    <div className="budget-bar">
                      <div
                        className={`budget-progress ${isOverBudget ? 'warning' : ''}`}
                        style={{ width: `${Math.min(budgetPercent, 100)}%` }}
                      ></div>
                    </div>
                  )}
                </div>
                )}
                  </>
                ) : (
                  /* Demographics View */
                  <div className="demographics-content">
                    {demographicsLoading[campaign.id] ? (
                      <div className="demographics-loading">{t('campaigns.loadingDemographics')}</div>
                    ) : demographicsData[campaign.id]?.has_data === false || (demographicsData[campaign.id]?.total_impressions || 0) === 0 ? (
                      <div className="demographics-no-data">
                        {t('campaigns.noDataYet')}
                      </div>
                    ) : demographicsData[campaign.id] ? (
                      <div className="demographics-charts">
                        {/* Age Chart - Pie */}
                        <div className="demographics-chart-section">
                          <div className="demographics-chart-title age-title">Age</div>
                          {(() => {
                            const targeting = demographicsData[campaign.id]!.targeting;
                            // Show all ages from targeting (even with 0 impressions), exclude Unknown
                            const ageData = demographicsData[campaign.id]!.age
                              .filter(item => item.range !== t('campaigns.unknown'))
                              .filter(item => !targeting?.age_ranges || targeting.age_ranges.includes(item.range));
                            const total = ageData.reduce((sum, item) => sum + item.impressions, 0);

                            // Recalculate percentages excluding Unknown and filtered items
                            const recalculatedData = ageData.map(item => ({
                              ...item,
                              percentage: total > 0 ? (item.impressions / total) * 100 : 0
                            }));

                            // Colors for age ranges. Sense: orange→purple. Dievo: lime→olive.
                            const colors = getBranding().edition !== 'sense'
                              ? ['#C8F032', '#A6D63A', '#7FA01E', '#5A7A14', '#3E5310', '#2E3D0C']
                              : ['#FF6B4A', '#F55850', '#E94B6C', '#D44B80', '#B84D94', '#9B4DA8'];

                            // Calculate donut segments
                            const size = 260;
                            const center = size / 2;
                            const outerRadius = 120;
                            const innerRadius = 60; // Inner hole radius
                            let currentAngle = -90; // Start from top

                            const segments = recalculatedData.map((item, index) => {
                              const angle = (item.percentage / 100) * 360;
                              const startAngle = currentAngle;
                              const endAngle = currentAngle + angle;
                              currentAngle = endAngle;

                              let path: string;

                              // Special case: full circle (100%) - SVG arc can't draw when start=end
                              if (angle >= 359.99) {
                                // Draw full donut using two arcs
                                path = `M ${center} ${center - outerRadius}
                                        A ${outerRadius} ${outerRadius} 0 1 1 ${center} ${center + outerRadius}
                                        A ${outerRadius} ${outerRadius} 0 1 1 ${center} ${center - outerRadius}
                                        M ${center} ${center - innerRadius}
                                        A ${innerRadius} ${innerRadius} 0 1 0 ${center} ${center + innerRadius}
                                        A ${innerRadius} ${innerRadius} 0 1 0 ${center} ${center - innerRadius} Z`;
                              } else {
                                // Calculate arc path for donut
                                const startRad = (startAngle * Math.PI) / 180;
                                const endRad = (endAngle * Math.PI) / 180;

                                // Outer arc points
                                const outerX1 = center + outerRadius * Math.cos(startRad);
                                const outerY1 = center + outerRadius * Math.sin(startRad);
                                const outerX2 = center + outerRadius * Math.cos(endRad);
                                const outerY2 = center + outerRadius * Math.sin(endRad);

                                // Inner arc points
                                const innerX1 = center + innerRadius * Math.cos(startRad);
                                const innerY1 = center + innerRadius * Math.sin(startRad);
                                const innerX2 = center + innerRadius * Math.cos(endRad);
                                const innerY2 = center + innerRadius * Math.sin(endRad);

                                const largeArc = angle > 180 ? 1 : 0;

                                // Donut path: outer arc -> line to inner -> inner arc (reverse) -> close
                                path = `M ${outerX1} ${outerY1} A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${outerX2} ${outerY2} L ${innerX2} ${innerY2} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${innerX1} ${innerY1} Z`;
                              }

                              // Use impressions directly
                              const impressions = item.impressions;

                              return {
                                range: item.range,
                                percentage: item.percentage,
                                reach: impressions,  // Now shows impressions, not reach
                                path,
                                color: colors[index % colors.length]
                              };
                            });

                            return (
                              <PieChartWithTooltip segments={segments} size={size} />
                            );
                          })()}
                        </div>

                        {/* Gender Chart - Vertical Bar */}
                        <div className="demographics-chart-section gender-section">
                          <div className="demographics-chart-title">{t('campaigns.gender')}</div>
                          {(() => {
                            const targeting = demographicsData[campaign.id]!.targeting;
                            // Show all genders from targeting (even with 0 impressions), exclude Unknown
                            const genderData = demographicsData[campaign.id]!.gender
                              .filter(g => g.gender !== t('campaigns.unknown'))
                              .filter(g => !targeting?.genders || targeting.genders.includes(g.gender));
                            const male = genderData.find(g => g.gender === t('campaigns.male'));
                            const female = genderData.find(g => g.gender === t('campaigns.female'));
                            const showMale = !!male;
                            const showFemale = !!female;
                            const maleImpr = male?.impressions || 0;
                            const femaleImpr = female?.impressions || 0;
                            const total = maleImpr + femaleImpr;

                            // If no data, show nothing
                            if (total === 0) {
                              return <div className="demographics-no-data-small">{t('campaigns.noData')}</div>;
                            }

                            const malePercent = total > 0 ? (maleImpr / total) * 100 : 0;
                            const femalePercent = total > 0 ? (femaleImpr / total) * 100 : 0;
                            const onlyMale = showMale && !showFemale;
                            const onlyFemale = showFemale && !showMale;
                            return (
                              <div className="gender-vertical-bar">
                                <span className="gender-label-v">{t('campaigns.female')}</span>
                                <div className="gender-bar-track-vertical">
                                  {showFemale && (
                                    <div
                                      className={`gender-bar-female-v ${onlyFemale ? 'gender-bar-only' : ''}`}
                                      style={{ height: `${femalePercent}%` }}
                                    >
                                      <span className="gender-bar-text-v">{femalePercent.toFixed(0)}%</span>
                                      <span className="gender-bar-tooltip-v">Female: {formatNumber(femaleImpr)}</span>
                                    </div>
                                  )}
                                  {showMale && (
                                    <div
                                      className={`gender-bar-male-v ${onlyMale ? 'gender-bar-only' : ''}`}
                                      style={{ height: `${malePercent}%` }}
                                    >
                                      <span className="gender-bar-text-v">{malePercent.toFixed(0)}%</span>
                                      <span className="gender-bar-tooltip-v">Male: {formatNumber(maleImpr)}</span>
                                    </div>
                                  )}
                                </div>
                                <span className="gender-label-v">{t('campaigns.male')}</span>
                              </div>
                            );
                          })()}
                        </div>

                      </div>
                    ) : (
                      <div className="demographics-empty">{t('campaigns.noDemographics')}</div>
                    )}
                  </div>
                )}

                {/* Card AI Section - Pacing Status */}
                {campaign.status !== 'draft' && !isSenseMax(campaign) && (() => {
                  const hasMediaPlan = (campaign.media_plan_groups_count ?? 0) > 0;
                  const hasBehind = ((campaign.settings_issue_count ?? 0) > 0) || ((campaign.budget_needed_count ?? 0) > 0);
                  const allPaused = hasMediaPlan && !hasBehind && (campaign.daily_paused_count ?? 0) > 0 &&
                    (campaign.daily_paused_count ?? 0) === (campaign.media_plan_groups_count ?? 0);

                  const cardClass = hasBehind ? 'danger' : allPaused ? 'paused' : 'ok';

                  // Message for OK status
                  const okMessage = hasMediaPlan
                    ? t('campaigns.allOnTrack', { count: campaign.media_plan_groups_count ?? 0 })
                    : t('campaigns.pacingOk');

                  return (
                    <div
                      className={`card-ai ${cardClass}`}
                      onClick={() => openAdGroupsPanel(campaign)}
                    >
                      <div className="ai-badge">
                        {hasBehind ? (
                          <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                            </svg>
                            {t('campaigns.pacingAlert')}
                          </>
                        ) : allPaused ? (
                          <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <rect x="6" y="4" width="4" height="16"/>
                              <rect x="14" y="4" width="4" height="16"/>
                            </svg>
                            {t('campaigns.pausedToday')}
                          </>
                        ) : (
                          <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                            </svg>
                            {t('campaigns.onTrack')}
                          </>
                        )}
                      </div>
                      <p className="ai-recommendation">
                        {hasBehind
                          ? (campaign.budget_needed_count ?? 0) > 0
                            ? `${campaign.budget_needed_count} group${(campaign.budget_needed_count ?? 0) > 1 ? 's need' : ' needs'} budget increase to reach target.`
                            : campaign.pacing_alerts && campaign.pacing_alerts.filter(a => a.metric).length > 0
                              ? (() => {
                                  const alerts = campaign.pacing_alerts!.filter(a => a.metric);
                                  const metric = alerts[0].metric === 'impressions' ? 'impr' : 'clicks';
                                  const totalExpected = alerts.reduce((s, a) => s + a.expected, 0);
                                  const totalDelivered = alerts.reduce((s, a) => s + a.delivered, 0);
                                  const groupCount = (campaign.settings_issue_count ?? 0);
                                  return <>{t('campaigns.groupsBehind', { count: groupCount })}<br/><span style={{opacity: 0.7}}>{t('campaigns.yesterdayLabel')} <strong>{totalDelivered.toLocaleString(currentLocale())}</strong> {metric} {t('campaigns.deliveredVs')} <strong>{totalExpected.toLocaleString(currentLocale())}</strong> {t('campaigns.expectedSuffix')}</span></>;
                                })()
                              : `${campaign.settings_issue_count ?? 0} group${(campaign.settings_issue_count ?? 0) > 1 ? 's are' : ' is'} behind pace. Check targeting settings.`
                          : allPaused
                            ? `${campaign.daily_paused_count} group${(campaign.daily_paused_count ?? 0) > 1 ? 's' : ''} reached daily target and paused. Will resume tomorrow.`
                            : okMessage
                        }
                        <span className="ai-action">{t('campaigns.viewDetails')}</span>
                      </p>
                    </div>
                  );
                })()}

                {/* Sense Max — platforms this campaign is running on (replaces the pacing block) */}
                {isSenseMax(campaign) && (
                  <div className="card-platforms">
                    <span className="card-platforms-label">{t('campaigns.runningOn')}</span>
                    <div className="card-platforms-logos">
                      {(() => {
                        const launchedP = (campaign.sense_max_platforms && campaign.sense_max_platforms.length
                          ? campaign.sense_max_platforms : ['Sense']);
                        const failedP = campaign.failed_platforms || [];
                        // Launched platforms + any platform that FAILED to launch (e.g. Google
                        // that didn't launch) — the failed one carries a red "!" marker.
                        const shown = [...launchedP, ...failedP.filter(p => !launchedP.includes(p))];
                        return shown.map((p, i) => {
                          const disc = (campaign.disconnected_platforms || []).includes(p);
                          const failed = failedP.includes(p);
                          return (
                            <span key={`${p}-${i}`}
                              className={`card-plat ${disc ? 'plat-disconnected' : ''} ${failed ? 'plat-failed' : ''}`}
                              title={failed ? t('campaigns.platformFailed')
                                : disc ? t('campaigns.cabinetOff') : undefined}>
                              <PlatformLogo name={p} />
                              {failed && <span className="plat-failed-mark" aria-label="launch failed">!</span>}
                            </span>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {filteredCampaigns.length === 0 && (
            <div className="empty-state">
              {t('campaigns.noneForFilter')}
            </div>
          )}
        </div>
      )}

      {/* Ad Groups Slide Panel */}
      <div className={`modal-overlay ${slidePanel.open ? 'active' : ''}`} onClick={closeAdGroupsPanel}>
        <div className="modal-container" onClick={(e) => { e.stopPropagation(); setOpenDropdown(null); }}>
          <div className="modal-header">
            <div className="modal-title-section">
              <h2 className="modal-title">{t('campaigns.adGroups')}</h2>
              <span className="modal-subtitle">{cleanName(slidePanel.campaign?.name)}</span>
            </div>
            <button className="modal-close" onClick={closeAdGroupsPanel}>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>
          </div>
          <div className="modal-content">
            {/* Loading overlay for ad group actions */}
            {Object.keys(actionLoading).some(k => k.startsWith('ag-') && actionLoading[k]) && (
              <div className="modal-loading-overlay">
                <div className="modal-loading-spinner"></div>
              </div>
            )}
            {adGroupsLoading ? (
              <div className="ad-groups-loading">{t('campaigns.loadingAdGroups')}</div>
            ) : adGroups.length === 0 ? (
              <div className="ad-groups-empty">{t('campaigns.noAdGroups')}</div>
            ) : (
              <table className="ad-groups-table">
                <thead>
                  <tr>
                    <th>{t('campaigns.adGroup')}</th>
                    <th>{t('campaigns.period')}</th>
                    <th>{t('campaigns.trend')}</th>
                    <th>{t('campaigns.metrics')}</th>
                    <th>{t('campaigns.budget')}</th>
                    {!isAnalyst && <th>{t('campaigns.actions')}</th>}
                  </tr>
                </thead>
                <tbody>
                  {[...adGroups]
                    .sort((a, b) => {
                      // Active first, then scheduled, then paused, then ended
                      const getEndDate = (ag: AdGroup) => ag.end_date || ag.last_date;
                      const isEndedA = getEndDate(a) && new Date(getEndDate(a)!) < new Date(new Date().toDateString());
                      const isEndedB = getEndDate(b) && new Date(getEndDate(b)!) < new Date(new Date().toDateString());
                      const statusA = isEndedA ? 'ended' : a.status;
                      const statusB = isEndedB ? 'ended' : b.status;
                      const order: Record<string, number> = { active: 0, scheduled: 1, paused: 2, ended: 3 };
                      return (order[statusA] ?? 0) - (order[statusB] ?? 0);
                    })
                    .map((adGroup) => {
                    // Determine effective status - if end_date is in the past, it's ended
                    const endDateStr = adGroup.end_date || adGroup.last_date;
                    const isEnded = endDateStr && new Date(endDateStr) < new Date(new Date().toDateString());
                    const effectiveStatus = isEnded ? 'ended' : adGroup.status;
                    const isInactive = effectiveStatus !== 'active' && effectiveStatus !== 'scheduled';

                    const pacingStatus = adGroup.pacing_status || 'normal';
                    const hasRecommendation = !!adGroup.recommended_budget;
                    const hasAlert = pacingStatus !== 'normal';

                    return (
                    <React.Fragment key={adGroup.id}>
                    <tr className={`${isInactive ? 'inactive' : ''} ${hasAlert ? 'has-alert' : ''}`}>
                      <td>
                        <div className="ad-group-name-cell">
                          <span className={`status-indicator ${effectiveStatus}`}>
                            {effectiveStatus === 'active' && <span className="status-dot live"></span>}
                            {effectiveStatus === 'scheduled' && (
                              <svg className="status-icon scheduled" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2zm3 18H5V8h14v11z"/>
                              </svg>
                            )}
                            {effectiveStatus === 'paused' && (
                              <svg className="status-icon paused" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                              </svg>
                            )}
                            {effectiveStatus === 'ended' && (
                              <svg className="status-icon ended" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                              </svg>
                            )}
                          </span>
                          <div className="ad-group-info">
                            <div className="ad-group-title">
                              {cleanName(adGroup.name)}
                              {!adGroup.ads_io_id && <span className="draft-badge">{t('campaigns.draft')}</span>}
                              {hasAlert && (
                                <span className={`pacing-badge ${pacingStatus === 'ahead' ? 'paused' : hasRecommendation ? 'info' : 'warning'}`}>
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                    {pacingStatus === 'ahead' ? (
                                      <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                                    ) : (
                                      <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                                    )}
                                  </svg>
                                </span>
                              )}
                            </div>
                            {(!adGroup.source || adGroup.source === 'dv360') && (
                              <div className="ad-group-subtitle">{adGroupTypeLabel(adGroup.type)} • {t('campaigns.creativesCount', { count: adGroup.creative_count })}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="date-range">
                          <div className="date-start">{formatDate(adGroup.start_date || adGroup.first_date)}</div>
                          <div className="date-end">{formatDate(adGroup.end_date || adGroup.last_date)}</div>
                        </div>
                      </td>
                      <td>
                        <AdGroupSparkline data={adGroup.trend} adGroupId={adGroup.id} />
                      </td>
                      <td>
                        <div className="metrics-cell">
                          <div className="metric-row">
                            <span className="metric-label">{t('campaigns.imprShort')}</span>
                            <span className="metric-value">{formatNumber(adGroup.impressions)}</span>
                          </div>
                          <div className="metric-row">
                            <span className="metric-label">{t('campaigns.clicks')}</span>
                            <span className="metric-value">{adGroup.clicks.toLocaleString(currentLocale())}</span>
                          </div>
                          <div className="metric-row">
                            {adGroup.conversions > 0 ? (
                              <>
                                <span className="metric-label">{t('campaigns.convShort')}</span>
                                <span className="metric-value">{adGroup.conversions.toLocaleString(currentLocale())}</span>
                              </>
                            ) : (
                              <>
                                <span className="metric-label">CTR</span>
                                <span className="metric-value">{adGroup.ctr.toFixed(2)}%</span>
                              </>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        {(() => {
                          // Campaigns that have Google/Meta data don't have a
                          // meaningful planned budget — show only fact (spend).
                          const externalCampaign = !!slidePanel.campaign?.has_external_data;
                          if (!externalCampaign && adGroup.budget) {
                            return (
                              <div className="budget-info">
                                <div className="budget-values">
                                  <span className="budget-spent"><CurrencyValue value={adGroup.spend} currency={adGroup.budget_currency || currency} short /></span>
                                  <span className="budget-total">/ <CurrencyValue value={adGroup.budget} currency={adGroup.budget_currency || currency} short /></span>
                                </div>
                                <div className="budget-bar">
                                  <div
                                    className="budget-progress"
                                    style={{ width: `${Math.min((adGroup.spend / adGroup.budget) * 100, 100)}%` }}
                                  ></div>
                                </div>
                              </div>
                            );
                          }
                          if (adGroup.spend > 0) {
                            return (
                              <div className="budget-info">
                                <div className="budget-values">
                                  <span className="budget-spent"><CurrencyValue value={adGroup.spend} currency={adGroup.budget_currency || currency} short /></span>
                                </div>
                              </div>
                            );
                          }
                          return <span className="no-budget">—</span>;
                        })()}
                      </td>
                      {!isAnalyst && (
                      <td>
                        {(!adGroup.source || adGroup.source === 'dv360') && (
                        <div className={`actions-dropdown ${openDropdown === `ag-${adGroup.id}` ? 'open' : ''}`}>
                          <button
                            className="actions-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDropdown(openDropdown === `ag-${adGroup.id}` ? null : `ag-${adGroup.id}`);
                            }}
                          >
                            <svg viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                            </svg>
                          </button>
                          <div className="actions-menu">
                            {!slidePanel.campaign?.is_locked && (<>
                            <button className="action-item" onClick={() => { setOpenDropdown(null); navigate(`/campaigns/${slidePanel.campaign?.id}/edit/${adGroup.id}`); }}>
                              <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                              </svg>
                              {t('campaigns.edit')}
                            </button>
                            <button
                              className="action-item"
                              disabled={isDuplicatingGroup}
                              onClick={() => handleDuplicateGroup(adGroup)}
                            >
                              <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                              </svg>
                              {isDuplicatingGroup ? t('campaigns.duplicating') : t('campaigns.duplicate')}
                            </button>
                            <div className="action-divider"></div>
                            {!adGroup.ads_io_id ? (
                              <>
                                {slidePanel.campaign?.status !== 'draft' && (() => {
                                  const canPublish = canPublishGroup(adGroup);
                                  return (
                                    <button
                                      className={`action-item publish ${!canPublish ? 'disabled' : ''}`}
                                      disabled={!canPublish}
                                      title={!canPublish ? t('campaigns.requiredMissing') : undefined}
                                    >
                                      <svg viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M5 4v2h14V4H5zm0 10h4v6h6v-6h4l-7-7-7 7z"/>
                                      </svg>
                                      {t('campaigns.publish')}
                                    </button>
                                  );
                                })()}
                                <button
                                  className="action-item delete"
                                  onClick={() => {
                                    setDeletingGroup(adGroup);
                                    setShowDeleteGroupModal(true);
                                    setOpenDropdown(null);
                                  }}
                                >
                                  <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                                  </svg>
                                  {t('campaigns.delete')}
                                </button>
                              </>
                            ) : (
                              <>
                                {effectiveStatus !== 'ended' && (
                                  <button
                                    className={`action-item ${effectiveStatus !== 'active' ? 'resume' : ''} ${actionLoading[`ag-${adGroup.id}`] ? 'loading' : ''}`}
                                    onClick={() => toggleAdGroupStatus(adGroup, effectiveStatus === 'active' ? 'paused' : 'active')}
                                    disabled={actionLoading[`ag-${adGroup.id}`]}
                                  >
                                    {effectiveStatus === 'active' ? (
                                      <svg viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                                      </svg>
                                    ) : (
                                      <svg viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M8 5v14l11-7z"/>
                                      </svg>
                                    )}
                                    {actionLoading[`ag-${adGroup.id}`] ? t('campaigns.loading') : (effectiveStatus === 'active' ? t('campaigns.pause') : t('campaigns.resume'))}
                                  </button>
                                )}
                                <button
                                  className="action-item delete"
                                  onClick={() => {
                                    setDeletingGroup(adGroup);
                                    setShowDeleteGroupModal(true);
                                    setOpenDropdown(null);
                                  }}
                                >
                                  <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                                  </svg>
                                  {t('campaigns.archive')}
                                </button>
                              </>
                            )}
                            </>)}
                          </div>
                        </div>
                        )}
                      </td>
                      )}
                    </tr>
                    {hasAlert && (
                      <tr className="alert-row">
                        <td colSpan={isAnalyst ? 5 : 6}>
                          <div className={`pacing-alert-card ${pacingStatus === 'ahead' ? 'paused' : 'danger'}`}>
                            <div className="alert-icon">
                              {pacingStatus === 'ahead' ? (
                                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                                  <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                                </svg>
                              ) : (
                                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                                  <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                                </svg>
                              )}
                            </div>
                            <div className="alert-content">
                              <span className="alert-message">
                                {pacingStatus === 'ahead'
                                  ? t('campaigns.dailyReached')
                                  : hasRecommendation
                                    ? t('campaigns.behindPaceIncrease', {
                                      amount: formatCurrency(adGroup.recommended_budget || 0, adGroup.budget_currency || currency),
                                    })
                                    : adGroup.pacing_alert?.metric
                                      ? <>{t('campaigns.behindPaceShort')} {t('campaigns.yesterdayLabel')} <strong>{adGroup.pacing_alert.delivered.toLocaleString(currentLocale())}</strong> {adGroup.pacing_alert.metric === 'impressions' ? t('campaigns.imprShortUnit') : t('campaigns.clicksShortUnit')} {t('campaigns.deliveredVs')} <strong>{adGroup.pacing_alert.expected.toLocaleString(currentLocale())}</strong> {t('campaigns.expectedSuffix')}</>
                                      : t('campaigns.behindPaceHint')}
                              </span>
                              {hasRecommendation && (
                                <button
                                  className="alert-action-btn"
                                  onClick={() => handleApplyBudgetRecommendation(adGroup)}
                                  disabled={applyingBudgetForGroup === adGroup.id}
                                >
                                  {applyingBudgetForGroup === adGroup.id ? t('campaigns.applying') : `Apply ${formatCurrency(adGroup.recommended_budget || 0, adGroup.budget_currency || currency)}`}
                                </button>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Delete/Archive Campaign Confirmation Modal */}
      {showDeleteModal && deletingCampaign && (() => {
        const isDraft = deletingCampaign.status === 'draft';
        const actionWord = isDraft ? t('campaigns.delete') : t('campaigns.archive');
        const actionWordLower = isDraft ? 'delete' : 'archive';
        return (
        <div className="modal-overlay" onClick={() => !isDeleting && setShowDeleteModal(false)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, visibility: 'visible' }}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-modal-icon danger">
              <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
              </svg>
            </div>
            <h3>{actionWord} Campaign?</h3>
            <p>
              This will permanently {actionWordLower} <strong>"{cleanName(deletingCampaign.name)}"</strong> and all its ad groups.
              <br /><br />
              <span style={{ color: '#ef4444' }}>{t('campaigns.cannotUndo')}</span>
            </p>
            <div className="confirm-modal-actions">
              <button
                className="btn-secondary"
                onClick={() => { setShowDeleteModal(false); setDeletingCampaign(null); }}
                disabled={isDeleting}
              >
                {t('campaigns.cancel')}
              </button>
              <button
                className="btn-danger"
                onClick={handleDeleteCampaign}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <span className="btn-spinner"></span>
                    {isDraft ? t('campaigns.deleting') : t('campaigns.archiving')}
                  </>
                ) : (
                  `${actionWord} Campaign`
                )}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Delete/Archive Group Confirmation Modal */}
      {showDeleteGroupModal && deletingGroup && (() => {
        const isPublished = !!deletingGroup.ads_io_id;
        const actionWord = isPublished ? t('campaigns.archive') : t('campaigns.delete');
        const actionWordLower = isPublished ? 'archive' : 'delete';
        return (
        <div className="modal-overlay" onClick={() => !isDeletingGroup && setShowDeleteGroupModal(false)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 1, visibility: 'visible' }}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-modal-icon danger">
              <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
              </svg>
            </div>
            <h3>{actionWord} Ad Group?</h3>
            <p>
              This will permanently {actionWordLower} <strong>"{cleanName(deletingGroup.name)}"</strong>.
              <br /><br />
              <span style={{ color: '#ef4444' }}>{t('campaigns.cannotUndo')}</span>
            </p>
            <div className="confirm-modal-actions">
              <button
                className="btn-secondary"
                onClick={() => { setShowDeleteGroupModal(false); setDeletingGroup(null); }}
                disabled={isDeletingGroup}
              >
                {t('campaigns.cancel')}
              </button>
              <button
                className="btn-danger"
                onClick={handleDeleteGroup}
                disabled={isDeletingGroup}
              >
                {isDeletingGroup ? (
                  <>
                    <span className="btn-spinner"></span>
                    {isPublished ? t('campaigns.archiving') : t('campaigns.deleting')}
                  </>
                ) : (
                  `${actionWord} Group`
                )}
              </button>
            </div>
          </div>
        </div>
        );
      })()}
    </>
  );
};

export default CampaignsPage;
